import { useMemo, useCallback, useEffect, useState, useRef, memo } from "react";
import { useApolloClient, gql } from "@apollo/client";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Form } from "@formio/react";
import dayjs from "dayjs";
import {
  Trash2,
  FileText,
  GripVertical,
  Upload,
  Info,
  File,
  X,
  ChevronDown,
  Download,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Rows2,
  TrendingUp,
  FileCheck2,
  Users,
  ShieldCheck,
  ClipboardList,
  Ban,
  Scale,
  Paperclip,
  Pencil,
  Copy,
  Check,
  ExternalLink,
  DownloadCloud,
} from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import { toast } from "react-toastify";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import {
  buildDocumentProductionList,
  ProductionDocument,
} from "./buildDocumentProductionList";
import { buildFormDocumentList } from "./buildFormDocumentList";
import { useGetContactOverview } from "@/api/contact/useGetContactOverview";
import { useGetExtendedContactDetails } from "@/api/contact/useGetExtendedContactDetails";
import { useLoadForm } from "@/api/docprod/useLoadForm";
import { useDownloadAttachment } from "@/api/docproducer/useDownloadAttachment";
import {
  useGetProducedDocuments,
  ProducedDocumentsTask,
} from "@/api/docproducer/useGetProducedDocuments";
import { useGetCompletedProcessTask } from "@/api/flowable/useGetCompletedProcessTask";
import {
  QueryContact,
  QueryModelPosition,
  QueryProfile,
  QuerySecurity,
} from "@/api/queries/types";
import { useGetSavedQuery2 } from "@/api/queries/useGetSavedQuery";
import { useDownloadReport } from "@/api/reports/useDownloadReport";
import { Contact, Portfolio } from "@/api/types";
import { Button } from "@/components/Button/Button";
// Dialog import removed — produce choice is now inline
import { LogoAnimation } from "@/components/Logo/logoAnimation";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/select";
import { SelectButton } from "@/components/select-button";
import { AutoSuit } from "@/forms/classes/AutoSuit";
import { SuitStatement } from "@/forms/classes/SuitStatement";
import { OrderForm } from "@/forms/forms/orders/orderForm/OrderForm";
import { OrderFormNew } from "@/forms/forms/orders/orderFormNew/OrderFormNew";
import { AdvisoryAgainstForm } from "@/forms/forms/quality/advisoryAgainst/AdvisoryAgainstForm";
import { KycOrgForm } from "@/forms/forms/quality/kycOrg/KycOrgForm";
import {
  CAPITAL_NEED_OPTIONS,
  NET_WEALTH_OPTIONS,
  DEBT_BURDEN_OPTIONS as ORG_DEBT_BURDEN_OPTIONS,
  ORG_INVESTMENT_HORIZON_OPTIONS,
  ORG_RISK_COMFORT_OPTIONS,
  ORG_LIQUIDITY_OPTIONS,
  ORG_INVESTMENT_GOAL_OPTIONS,
} from "@/forms/forms/quality/kycOrg/KycOrgForm.options";
import { KycPrivForm } from "@/forms/forms/quality/kycPriv/KycPrivForm";
import {
  ESG_PREF_OPTIONS,
  ESG_PREF_TYPE_OPTIONS,
  GROSS_ANNUAL_INCOME_OPTIONS,
  INCOME_STABILITY_OPTIONS,
  INVESTMENT_GOAL_OPTIONS,
  INVESTMENT_HORIZON_OPTIONS,
  KNOWLEDGE_COLUMNS,
  EXPERIENCE_COLUMNS,
  TRADING_FREQUENCY_COLUMNS,
  PERIOD_COLUMNS,
  LIQUIDITY_OPTIONS,
  MIFID_EXPERIENCE_EDUCATION_OPTIONS,
  MIFID_INSTRUMENT_ROWS,
  RISK_COMFORT_OPTIONS,
  MARKET_DROP_RESPONSE_OPTIONS,
  VALUE_DECLINE_IMPACT_OPTIONS,
} from "@/forms/forms/quality/kycPriv/KycPrivForm.options";
import { MeetingMinutesForm } from "@/forms/forms/quality/meetingMinutes/MeetingMinutesForm";
import { OpenCustomerInstructionForm } from "@/forms/forms/quality/openCustomerInstruction/OpenCustomerInstructionForm";
import { SuitabilityForm } from "@/forms/forms/quality/suitability/SuitabilityForm";
import {
  computeSuitabilityDocParams,
  calcStockPartsFromPortfolios,
} from "@/forms/forms/quality/suitability/SuitabilityForm.rules";
import { PendingOrder } from "@/forms/forms/quality/suitability/SuitabilityForm.types";
import { SuitabilityNewForm } from "@/forms/forms/quality/suitabilityNew/SuitabilityNewForm";
import { cn } from "@/lib/utils";
import {
  ContactEntry,
  DocProducerOverrides,
  useDocProducer,
  useDocProducerUI,
} from "@/providers/DocProducerProvider";
import { useKeycloak } from "@/providers/KeycloakProvider";
import { DocProducerDocument } from "@/views/docproducer";
import { FormView } from "@/views/form/form";

// ── Production process mutation (run imperatively — no form UI shown) ────────
const PRODUCTION_START_PROCESS = gql`
  mutation ProductionStartProcess($key: String, $data: Json) {
    startProcess(key: $key, data: $data) {
      formDefinition
      taskId
      processInstanceId
      data
    }
  }
`;

type ProfileMap = Record<string, string | number | boolean | null>;
export function buildProfileMap(profileData: QueryProfile[]): ProfileMap {
  const map: ProfileMap = {};
  for (const p of profileData) {
    let value: string | number | boolean | null = null;
    switch (p.primaryType ?? "") {
      case "java.lang.Boolean":
        value = p.booleanValue;
        break;
      case "java.lang.Double":
        value = p.doubleValue;
        break;
      case "java.lang.Integer":
      case "java.lang.Long":
        value = p.intValue;
        break;
      case "java.util.Date":
        value = p.dateValue;
        break;
      default:
        value = p.stringValue ?? null;
    }
    map[p.attributeKey] = value;
    // Also index by short key (strip namespace like contact.cgProfiling.)
    const short = p.attributeKey.replace(/^contact\.[^.]+\./, "");
    if (short !== p.attributeKey) map[short] = value;
  }
  return map;
}

function parseOrderAmount(value: unknown): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (value === null || value === undefined || value === "") return 0;
  const normalized = String(value).replace(/\s/g, "").replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function computeNoModelStockFraction(
  buyRows: Array<{
    instrumentName?: string;
    instrumentCode?: string;
    class1code?: string;
    class2code?: string;
    amountInput?: string | number;
  }>
): number {
  const filledRows = buyRows.filter(
    (r) =>
      (r.instrumentName ?? "").trim() !== "" ||
      (r.instrumentCode ?? "").trim() !== ""
  );
  const totalBuy = filledRows.reduce(
    (sum, r) => sum + parseOrderAmount(r.amountInput),
    0
  );
  if (totalBuy <= 0) return 0;

  const stockBuy = filledRows
    .filter((r) => {
      const class1 = String(r.class1code ?? "").toUpperCase();
      const class2 = String(r.class2code ?? "").toUpperCase();
      return class1 === "E" || class2 === "S" || class2 === "EF";
    })
    .reduce((sum, r) => sum + parseOrderAmount(r.amountInput), 0);

  return stockBuy / totalBuy;
}

function findSecurityForOrderRow(
  securities: QuerySecurity[] | undefined,
  instrumentCode: string,
  instrumentName: string
): QuerySecurity | null {
  const list = securities ?? [];
  const code = instrumentCode.trim().toUpperCase();
  const name = instrumentName.trim().toUpperCase();

  if (code) {
    const byCode = list.find(
      (s) =>
        String(s.security_code ?? "")
          .trim()
          .toUpperCase() === code
    );
    if (byCode) return byCode;
  }

  if (name) {
    const byName = list.find(
      (s) =>
        String(s.name ?? "")
          .trim()
          .toUpperCase() === name
    );
    if (byName) return byName;
  }

  return null;
}

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface UploadedDocument {
  file: File;
  stream: string;
}

interface DocProducerContentProps {
  selectedContact?: QueryContact | null;
}
interface DocProducerData {
  formKey: string;
  title: string;
  toProduction: boolean;
  extraValues?: Record<string, unknown>;
  inpData?: Record<string, unknown>;
}
interface TaskAttachment {
  taskId: string;
  name: string;
}

interface Signee {
  id: number;
  contactId: string;
  name: string;
}

interface NewOrderDocument {
  id: string;
  docType: string;
  data?: {
    newData: {
      dpPortSelector: {
        name: string;
      };
      dpModelType: {
        label: string;
      };
      dpAmountToInvest: number;
      dpOneOffAmount: number;
    };
  };
}

// ── Per-docType visual config ────────────────────────────────────────────────
const DOC_TYPE_CONFIG: Record<
  string,
  {
    icon: React.ElementType;
    iconBg: string;
    iconColor: string;
    borderColor: string;
  }
> = {
  order: {
    icon: TrendingUp,
    iconBg: "bg-blue-50 dark:bg-blue-950/40",
    iconColor: "text-blue-600 dark:text-blue-400",
    borderColor: "border-l-blue-500",
  },
  meetingMinutes: {
    icon: Users,
    iconBg: "bg-violet-50 dark:bg-violet-950/40",
    iconColor: "text-violet-600 dark:text-violet-400",
    borderColor: "border-l-violet-500",
  },
  suitabilityStatement: {
    icon: FileCheck2,
    iconBg: "bg-emerald-50 dark:bg-emerald-950/40",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    borderColor: "border-l-emerald-500",
  },
  docSuitabilityNew: {
    icon: FileCheck2,
    iconBg: "bg-emerald-50 dark:bg-emerald-950/40",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    borderColor: "border-l-emerald-500",
  },
  quality: {
    icon: ShieldCheck,
    iconBg: "bg-amber-50 dark:bg-amber-950/40",
    iconColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-l-amber-500",
  },
  customerAgreement: {
    icon: ShieldCheck,
    iconBg: "bg-amber-50 dark:bg-amber-950/40",
    iconColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-l-amber-500",
  },
  openCustomerInstruction: {
    icon: ClipboardList,
    iconBg: "bg-sky-50 dark:bg-sky-950/40",
    iconColor: "text-sky-600 dark:text-sky-400",
    borderColor: "border-l-sky-500",
  },
  advisoryAgainst: {
    icon: Ban,
    iconBg: "bg-rose-50 dark:bg-rose-950/40",
    iconColor: "text-rose-600 dark:text-rose-400",
    borderColor: "border-l-rose-500",
  },
  uploaded: {
    icon: Paperclip,
    iconBg: "bg-gray-100 dark:bg-gray-800",
    iconColor: "text-gray-500 dark:text-gray-400",
    borderColor: "border-l-gray-400",
  },
  documentFromSecurity: {
    icon: FileCheck2,
    iconBg: "bg-teal-50 dark:bg-teal-950/40",
    iconColor: "text-teal-600 dark:text-teal-400",
    borderColor: "border-l-teal-500",
  },
};

const DEFAULT_DOC_CONFIG = {
  icon: FileText,
  iconBg: "bg-gray-100 dark:bg-gray-800",
  iconColor: "text-gray-500 dark:text-gray-400",
  borderColor: "border-l-gray-400",
};

// Parses signing links from a task description (HTML format from Flowable)
const parseSigningInfo = (
  description: string | null | undefined
): Array<{ name: string; contactId: string; link: string; status: string }> => {
  if (!description) return [];
  const signers: Array<{
    name: string;
    contactId: string;
    link: string;
    status: string;
  }> = [];
  const regex =
    /<li>([^(]+)\(([^)]+)\):[^<]*<a href='([^']+)'[^>]*>.*?Status:\s*(\w+)<\/li>/gi;
  let match;
  while ((match = regex.exec(description)) !== null) {
    signers.push({
      name: match[1].trim(),
      contactId: match[2].trim(),
      link: match[3].trim(),
      status: match[4].trim(),
    });
  }
  return signers;
};

// Memoized document item component — one grid row shape shared by every document type.
const DocumentItem = memo(
  ({
    doc,
    onDelete,
    onEdit,
  }: {
    doc: DocProducerDocument;
    onDelete: (doc: DocProducerDocument) => void;
    onEdit?: (doc: DocProducerDocument) => void;
  }) => {
    type MetaItem = { label?: string; value: string; mono?: boolean };

    const renderMeta = (items: (MetaItem | null)[]) => {
      const visible = items.filter((item): item is MetaItem => !!item);
      if (visible.length === 0) {
        return (
          <span className="text-[11px] text-gray-400 dark:text-gray-500">
            Ingen detaljer registrert
          </span>
        );
      }
      return (
        <dl className="grid min-w-0 grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
          {visible.map((item, i) => (
            <div
              key={`${item.label ?? "value"}-${i}`}
              className="flex min-w-0 items-baseline gap-2"
            >
              {item.label && (
                <dt className="shrink-0 text-[9px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                  {item.label}
                </dt>
              )}
              <dd
                className={cn(
                  "min-w-0 truncate text-[11px] font-medium text-gray-700 dark:text-gray-200",
                  item.mono && "font-mono"
                )}
                title={item.value}
              >
                {item.value}
              </dd>
            </div>
          ))}
        </dl>
      );
    };

    // ── Resolve title & meta pills per docType ───────────────────────────────

    let title = doc.processName || doc.docType;
    let metaItems: (MetaItem | null)[] = [];

    if (doc.docType === "uploaded") {
      const u = doc as typeof doc & { data?: { file?: File }; desc?: string };
      title = "Vedlegg";
      metaItems = [
        u.desc ? { label: "Fil", value: u.desc, mono: true } : null,
        u.data?.file
          ? {
              label: "Størrelse",
              value:
                u.data.file.size > 1024 * 1024
                  ? `${(u.data.file.size / (1024 * 1024)).toFixed(2)} MB`
                  : `${(u.data.file.size / 1024).toFixed(2)} KB`,
            }
          : null,
      ];
    } else if (doc.docType === "order") {
      const raw = doc.data as Record<string, unknown> | undefined;
      const nd = (raw?.newData as Record<string, unknown> | undefined) ?? raw;
      const portfolioName =
        (nd?._portfolioName as string) ||
        (raw?.dpPortSelector as { name?: string } | undefined)?.name;
      const modelType =
        (nd?.dpModelType as string) ||
        (raw?.dpModelType as { label?: string } | undefined)?.label;
      const modelName = nd?.dpModelTypeName as string | undefined;
      const monthly =
        (nd?.dpAmountToInvest as number) ?? (raw?.dpAmountToInvest as number);
      const oneOff =
        (nd?.dpOnOffAmount as number) ?? (raw?.dpOneOffAmount as number);
      const withdrawal =
        (nd?.dpWithdrawal as number) ?? (raw?.withdrawal as number);
      const portId =
        (nd?.dpPortfolioId as string) ?? (raw?.portfolioId as string);
      const feeType = (nd?.dpFeeType as string) ?? (raw?.feeType as string);

      title =
        portId === "NEW"
          ? "Ny portefølje"
          : portfolioName || "Eksisterende portefølje";

      metaItems = [
        modelName || modelType
          ? { label: "Modell", value: modelName || modelType || "" }
          : null,
        feeType === "all"
          ? { label: "Avtale", value: "Alle tjenester" }
          : feeType === "onlyFund"
          ? { label: "Avtale", value: "Kun fond" }
          : null,
        monthly
          ? {
              label: "Investering",
              value: `${monthly.toLocaleString("nb-NO")} kr`,
            }
          : null,
        oneOff
          ? {
              label: "Engangsbeløp",
              value: `${oneOff.toLocaleString("nb-NO")} kr`,
            }
          : null,
        withdrawal
          ? {
              label: "Uttak",
              value: `${withdrawal.toLocaleString("nb-NO")} kr`,
            }
          : null,
      ];
    } else if (doc.docType === "meetingMinutes") {
      title = "Møtereferat";
      const nd = (doc.data as Record<string, unknown> | undefined)?.newData as
        | Record<string, unknown>
        | undefined;
      const rawDate = nd?.dpdocRefMeetDate as string | undefined;
      metaItems = [
        rawDate
          ? {
              label: "Dato",
              value: rawDate.replace("T", " ").slice(0, 16),
              mono: true,
            }
          : null,
        nd?.dpdocRefMeetPlace
          ? { label: "Sted", value: nd.dpdocRefMeetPlace as string }
          : null,
        nd?.dpDocRefMeetPart
          ? { label: "Deltakere", value: nd.dpDocRefMeetPart as string }
          : null,
      ];
    } else if (
      doc.docType === "suitabilityStatement" ||
      doc.docType === "docSuitabilityNew"
    ) {
      title =
        doc.docType === "suitabilityStatement"
          ? "Egnethetsattest"
          : "Suitability (NEW)";
      const nd = (doc.data as Record<string, unknown> | undefined)?.newData as
        | Record<string, unknown>
        | undefined;
      metaItems = [
        nd?.dpInvTime
          ? { label: "Tidshorisont", value: nd.dpInvTime as string }
          : null,
        nd?.dpInitStockPart
          ? { label: "Aksjeandel før", value: `${nd.dpInitStockPart} %` }
          : null,
        nd?.dpFinStockPart
          ? { label: "Aksjeandel etter", value: `${nd.dpFinStockPart} %` }
          : null,
      ];
    } else if (
      doc.docType === "quality" ||
      doc.docType === "customerAgreement"
    ) {
      title = doc.processName || "Kvalitetsdokument";
    }

    const cfg = DOC_TYPE_CONFIG[doc.docType] ?? DEFAULT_DOC_CONFIG;
    const DocIcon = cfg.icon;
    // border-l-* utility reused as a bg-* accent bar so it never shifts row content out of the shared grid.
    const accentColor = cfg.borderColor.replace("border-l-", "bg-");
    const actionButtonClass =
      "inline-flex size-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-200";

    return (
      <div className="group relative grid grid-cols-1 items-center gap-2 border-b border-gray-200 py-3 pl-5 pr-4 transition-colors last:border-b-0 hover:bg-gray-50/80 dark:border-gray-700 dark:hover:bg-gray-900/40 md:grid-cols-[minmax(0,1.6fr)_minmax(0,1.4fr)_96px] md:gap-4">
        <span
          className={cn("absolute inset-y-0 left-0 w-1", accentColor)}
          aria-hidden="true"
        />

        {/* Document */}
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={cn(
              "flex size-9 flex-shrink-0 items-center justify-center rounded-lg ring-1 ring-black/5 dark:ring-white/10",
              cfg.iconBg
            )}
          >
            <DocIcon size={16} className={cfg.iconColor} />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold leading-tight text-gray-900 dark:text-gray-100">
              {title}
            </p>
            <span className="text-[9px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
              {DOC_TYPE_LABELS[doc.docType] ?? doc.docType}
            </span>
          </div>
        </div>

        {/* Details */}
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {renderMeta(metaItems)}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-1">
          {doc.docType === "order" && onEdit && (
            <button
              type="button"
              onClick={() => onEdit(doc)}
              className={cn(
                actionButtonClass,
                "hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-950/30 dark:hover:text-blue-400"
              )}
              title="Rediger ordre"
              aria-label="Rediger ordre"
            >
              <Pencil size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => onDelete(doc)}
            className={cn(
              actionButtonClass,
              "hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
            )}
            title="Slett dokument"
            aria-label="Slett dokument"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    );
  }
);

DocumentItem.displayName = "DocumentItem";

// Generic loader for any Formio form fetched via useLoadForm.
// Extracted as its own component so hooks can be called unconditionally.
interface LoadFormRunnerProps {
  formId: number;
  contactValues: ContactEntry;
  securities: QuerySecurity[];
  profileData: QueryProfile[];
  onDocumentCreated: (doc: DocProducerDocument) => void;
  onClose: () => void;
}

const getFeeType = (tagsAsSet: string[]) => {
  const feeType = tagsAsSet.find((tag) => tag.startsWith("Forvaltningsavtale"));
  switch (feeType) {
    case "Forvaltningsavtale-Aktiv":
      return "all";
    case "Forvaltningsavtale-Komplett":
      return "all";
    case "Forvaltningsavtale-Kun fond":
      return "only fund";
    default:
      return "";
  }
};

const LoadFormRunner = ({
  formId,
  contactValues,
  securities,
  profileData,
  onDocumentCreated,
  onClose,
}: LoadFormRunnerProps) => {
  const { data, loading } = useLoadForm(formId);

  const formSchema = useMemo(
    () =>
      data?.loadForm?.formDefinition
        ? { ...JSON.parse(data.loadForm.formDefinition), action: "" }
        : null,
    [data?.loadForm?.formDefinition]
  );

  // Pre-populate submission with context data derived from the contact
  const submission = useMemo(
    () => ({
      data: {
        contact: contactValues.contact,
        securities: securities,
        profile: profileData,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contactValues.contact?.id]
  );

  if (loading)
    return (
      <div className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <LogoAnimation size={20} />
        Loading form…
      </div>
    );
  if (!formSchema)
    return (
      <div className="p-4 text-sm text-muted-foreground">No form data.</div>
    );

  return (
    <div className="h-full overflow-auto p-4">
      <Form
        form={formSchema}
        submission={submission}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        formReady={(formioInstance: any) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          formioInstance.on("submit", (sub: any) => {
            onDocumentCreated({
              id: `form_${formId}_${new Date().toISOString()}`,
              docType: "order",
              processName: data?.loadForm?.formName ?? "Form",
              desc: data?.loadForm?.formName ?? "Form",
              data: { ...submission.data, ...sub.data },
            });
            formioInstance.emit("submitDone", sub);
            onClose();
          });
        }}
      />
    </div>
  );
};

// ProduceChoiceView component — inline alternative selection view
interface ProduceChoiceViewProps {
  contactValues: ContactEntry | undefined;
  documents: DocProducerDocument[];
  advisorData: Contact | undefined | null;
  modelsData: QueryModelPosition[];
  securities: QuerySecurity[];
  profileData: QueryProfile[];
  onPreview: (list: ProductionDocument[]) => void;
  onSign: (list: ProductionDocument[]) => void;
}

const ProduceChoiceView = ({
  contactValues,
  documents,
  advisorData,
  modelsData,
  securities,
  profileData,
  onPreview,
  onSign,
}: ProduceChoiceViewProps) => {
  const buildList = () => {
    if (!contactValues?.contact) return [];
    const activePortfolios = (contactValues.contact.portfolios ?? []).filter(
      (p) => p.status === "A"
    );
    const pendingOrders: PendingOrder[] = documents
      .filter((d) => d.docType === "order")
      .map((d) => {
        const raw = d.data as Record<string, unknown>;
        const nd = (raw.newData ?? {}) as Record<string, unknown>;
        const ed = (raw.existData ?? {}) as Record<string, unknown>;
        const modelType = String(nd.dpModelType ?? "");
        const buyRows =
          (nd.buyOrders as
            | Array<{
                instrumentName?: string;
                instrumentCode?: string;
                class1code?: string;
                class2code?: string;
                amountInput?: string | number;
              }>
            | undefined) ?? [];
        const sellRows =
          (nd.sellOrders as Array<{
            name?: string;
            instrumentName?: string;
          }>) ?? [];
        const instrumentNames = Array.from(
          new Set(
            [
              ...buyRows.map((r) => (r.instrumentName ?? "").trim()),
              ...sellRows.map((r) => (r.instrumentName ?? r.name ?? "").trim()),
            ].filter(Boolean)
          )
        );
        const manualOrderRows = buyRows
          .map((r) => {
            const instrumentCode = String(
              (r as { instrumentCode?: string }).instrumentCode ?? ""
            ).trim();
            const name = (
              r.instrumentName ??
              (r as { instrumentCode?: string }).instrumentCode ??
              ""
            ).trim();
            const amountNok = parseOrderAmount(
              (r as { amountInput?: string | number }).amountInput
            );
            const class1 = String(
              (r as { class1code?: string }).class1code ?? ""
            ).toUpperCase();
            const class2 = String(
              (r as { class2code?: string }).class2code ?? ""
            ).toUpperCase();
            const isStock = class1 === "E" || class2 === "S" || class2 === "EF";
            const security = findSecurityForOrderRow(
              securities,
              instrumentCode,
              name
            );
            return { instrumentName: name, amountNok, isStock, security };
          })
          .filter((r) => r.instrumentName && r.amountNok > 0);
        const stockFraction =
          !modelType || modelType === "NOMODEL"
            ? computeNoModelStockFraction(buyRows)
            : undefined;
        return {
          portfolioId: String(nd.dpPortfolioId ?? ""),
          portfolioName: String(
            nd.dpPortfolioName ?? ed.dpPortfolioName ?? "Portefolje"
          ),
          portfolioType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
          isNew: !(ed.isExisting as boolean),
          portType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
          amountToInvest: Number(nd.dpAmountToInvest ?? 0),
          oneOffAmount: Number(nd.dpOnOffAmount ?? 0),
          sumAvCash: Number(ed.dpSumAvCash ?? 0),
          sumInsValue: Number(ed.dpSumInsValue ?? 0),
          withdrawal: Number(nd.dpWithdrawal ?? 0),
          modelType,
          modelTypeName: String(nd.dpModelTypeName ?? ""),
          instrumentNames,
          manualOrderRows,
          managementFeePct: Number(nd.dpManagementFee ?? 0),
          subscriptionFeePct: Number(nd.dpOnOffPercentage ?? 0),
          feeType: String(nd.dpFeeType ?? ""),
          stockFraction,
        };
      });
    return buildDocumentProductionList(
      contactValues.contact,
      advisorData,
      documents,
      modelsData,
      securities,
      profileData,
      activePortfolios,
      pendingOrders
    );
  };

  const handlePreview = () => {
    const list = buildList();
    onPreview(list);
  };

  const handleSign = () => {
    const list = buildList();
    onSign(list);
  };

  return (
    <div className="h-full flex items-start justify-center p-8">
      <div className="w-full max-w-sm space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Produser dokumenter
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Hva ønsker du å gjøre med dokumentene?
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <Button
            variant="default"
            className="w-full justify-start gap-3 h-14 text-left"
            onClick={handlePreview}
            icon={<FileCheck2 size={20} />}
            iconPosition="start"
          >
            <div>
              <div className="font-medium">Forhåndsvisning</div>
              <div className="text-xs font-normal opacity-75">
                Generer og forhåndsvis dokumentene
              </div>
            </div>
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start gap-3 h-14 text-left"
            onClick={handleSign}
            icon={<Paperclip size={20} />}
            iconPosition="start"
          >
            <div>
              <div className="font-medium">Send til signering</div>
              <div className="text-xs font-normal opacity-75">
                Send dokumentene til elektronisk signering
              </div>
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

// ProductionListView component
const DOC_TYPE_LABELS: Record<string, string> = {
  accountAgreement: "Kontoinngåelse",
  managementAgreement: "Forvaltningsavtale",
  modelAgreement: "Modellmandat",
  terms: "Vilkår",
  oneOff: "Engangsinnbetaling",
  withdrawal: "Uttak",
  manualOrder: "Ordreblankett",
  savingsOrder: "Månedlig sparing",
  meetingMinutes: "Møtereferat",
  suitabilityStatement: "Egnethetsattest",
  docSuitabilityNew: "Egnethetsattest (NEW)",
  customerAgreement: "Kundeavtale",
  openCustomerInstruction: "Åpen kundeinstruks",
  advisoryAgainst: "Frarådning",
  uploaded: "Vedlegg",
  documentFromSecurity: "Dokument (kundeprofil)",
};

const SortableProductionItem = ({
  pd,
  isSelected,
  onSelect,
}: {
  pd: ProductionDocument;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pd.id + "_" + pd.type });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <ProductionItemContent
        pd={pd}
        isSelected={isSelected}
        onSelect={onSelect}
        listeners={listeners}
      />
    </div>
  );
};

const ProductionItemContent = ({
  pd,
  isSelected,
  onSelect,
  listeners,
}: {
  pd: ProductionDocument;
  isSelected: boolean;
  onSelect: () => void;
  listeners?: Record<string, unknown>;
}) => {
  const cfg = DOC_TYPE_CONFIG[pd.type] ?? DEFAULT_DOC_CONFIG;
  const DocIcon = cfg.icon;
  return (
    <button
      onClick={onSelect}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-2.5 text-left border-l-2 transition-colors",
        isSelected
          ? "border-l-primary bg-primary/5 dark:bg-primary/10"
          : "border-l-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60"
      )}
    >
      <div
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="cursor-grab active:cursor-grabbing text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 flex-shrink-0"
      >
        <GripVertical size={13} />
      </div>
      <div
        className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          cfg.iconBg
        )}
      >
        <DocIcon size={14} className={cfg.iconColor} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate leading-tight">
          {pd.docName}
        </p>
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">
          {DOC_TYPE_LABELS[pd.type] ?? pd.type}
        </p>
      </div>
      <span className="flex-shrink-0 text-[10px] font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 rounded px-1.5 py-0.5 leading-none">
        {Object.keys(pd.params).length}
      </span>
    </button>
  );
};

SortableProductionItem.displayName = "SortableProductionItem";

const ProductionListView = ({
  productionDocList,
  onProduce,
  actionLabel = "Produser",
  signees,
}: {
  productionDocList: ProductionDocument[];
  onProduce: (
    selectedSignees: Signee[],
    orderedList: ProductionDocument[]
  ) => void;
  actionLabel?: string;
  signees?: Signee[];
}) => {
  const [orderedList, setOrderedList] = useState(() => productionDocList);
  const pdUid = (pd: ProductionDocument) => pd.id + "_" + pd.type;
  const [selectedId, setSelectedId] = useState<string | null>(
    productionDocList.length > 0 ? pdUid(productionDocList[0]) : null
  );
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const selected = orderedList.find((pd) => pdUid(pd) === selectedId) ?? null;
  const activeDragDocument = activeDragId
    ? orderedList.find((pd) => pdUid(pd) === activeDragId) ?? null
    : null;

  // Signees must be explicitly opted in — nobody is selected by default.
  const [selectedSigneeIds, setSelectedSigneeIds] = useState<Set<number>>(
    () => new Set()
  );

  const toggleSignee = (id: number) =>
    setSelectedSigneeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const listSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleListDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = orderedList.findIndex(
        (pd) => pdUid(pd) === String(active.id)
      );
      const newIndex = orderedList.findIndex(
        (pd) => pdUid(pd) === String(over.id)
      );
      if (oldIndex >= 0 && newIndex >= 0) {
        setOrderedList((prev) => arrayMove(prev, oldIndex, newIndex));
      }
    }
    setActiveDragId(null);
  };

  const handleListDragStart = (event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  };

  // Group params into key-value pairs, skipping empty values
  const paramEntries = selected
    ? Object.entries(selected.params).filter(
        ([, v]) => v !== null && v !== undefined && String(v).trim() !== ""
      )
    : [];

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Left sidebar — document list */}
        <div className="w-96 flex-shrink-0 flex flex-col border-r border-gray-200 dark:border-gray-700">
          {/* Sidebar header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Documents
            </span>
            <span className="text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-full px-2 py-0.5 leading-none">
              {productionDocList.length}
            </span>
          </div>
          {/* List */}
          <div className="flex-1 min-w-0 overflow-x-hidden overflow-y-auto py-1">
            <DndContext
              sensors={listSensors}
              collisionDetection={closestCenter}
              onDragStart={handleListDragStart}
              onDragEnd={handleListDragEnd}
              onDragCancel={() => setActiveDragId(null)}
            >
              <SortableContext
                items={orderedList.map((pd) => pdUid(pd))}
                strategy={verticalListSortingStrategy}
              >
                {orderedList.map((pd) => (
                  <SortableProductionItem
                    key={pdUid(pd)}
                    pd={pd}
                    isSelected={pdUid(pd) === selectedId}
                    onSelect={() => setSelectedId(pdUid(pd))}
                  />
                ))}
              </SortableContext>
              <DragOverlay dropAnimation={null}>
                {activeDragDocument ? (
                  <div className="w-96 max-w-[calc(100vw-2rem)] rounded-md bg-background shadow-xl">
                    <ProductionItemContent
                      pd={activeDragDocument}
                      isSelected={pdUid(activeDragDocument) === selectedId}
                      onSelect={() => undefined}
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>

        {/* Signees column — dedicated list, one row per person */}
        {signees && signees.length > 0 && (
          <div className="relative w-64 flex-shrink-0 flex flex-col border-r border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Signees
              </span>
              <span
                className={cn(
                  "text-[10px] font-medium rounded-full px-2 py-0.5 leading-none",
                  selectedSigneeIds.size === 0
                    ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400"
                    : "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400"
                )}
              >
                {selectedSigneeIds.size}/{signees.length}
              </span>
            </div>
            <div className="relative flex-1 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
              {signees.map((s) => {
                const active = selectedSigneeIds.has(s.id);
                return (
                  <button
                    key={s.id}
                    onClick={() => toggleSignee(s.id)}
                    className="w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/60"
                  >
                    <div
                      className={cn(
                        "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors",
                        active
                          ? "bg-blue-100 dark:bg-blue-900/40"
                          : "bg-gray-100 dark:bg-gray-800"
                      )}
                    >
                      <span
                        className={cn(
                          "text-[11px] font-bold uppercase leading-none",
                          active
                            ? "text-blue-600 dark:text-blue-400"
                            : "text-gray-400 dark:text-gray-500"
                        )}
                      >
                        {s.name.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate leading-tight">
                        {s.name}
                      </p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 truncate leading-tight mt-0.5">
                        {s.contactId}
                      </p>
                    </div>
                    <div
                      className={cn(
                        "w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border transition-colors",
                        active
                          ? "bg-blue-600 border-blue-600"
                          : "bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600"
                      )}
                    >
                      {active && (
                        <Check
                          size={11}
                          className="text-white"
                          strokeWidth={3}
                        />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            <div
              className={cn(
                "pointer-events-none absolute bottom-3 left-1/2 z-10 -translate-x-1/2 transition-all duration-300 ease-out",
                selectedSigneeIds.size === 0
                  ? "translate-y-0 opacity-100"
                  : "translate-y-4 opacity-0"
              )}
              aria-hidden={selectedSigneeIds.size > 0}
              role="status"
            >
              <div className="relative whitespace-nowrap rounded-md bg-gray-900 px-3 py-2 text-[11px] font-medium text-white shadow-lg dark:bg-gray-800">
                <span className="absolute -top-1.5 left-1/2 h-0 w-0 -translate-x-1/2 border-x-[6px] border-b-[6px] border-x-transparent border-b-gray-900 dark:border-b-gray-800" />
                Velg minst én signant
              </div>
            </div>
          </div>
        )}

        {/* Right — parameter inspector */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0 bg-gray-50/40 dark:bg-gray-900/30">
          {selected ? (
            <>
              {/* Inspector header */}
              <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 bg-white dark:bg-gray-900">
                {(() => {
                  const cfg =
                    DOC_TYPE_CONFIG[selected.type] ?? DEFAULT_DOC_CONFIG;
                  const DocIcon = cfg.icon;
                  return (
                    <div
                      className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                        cfg.iconBg
                      )}
                    >
                      <DocIcon size={16} className={cfg.iconColor} />
                    </div>
                  );
                })()}
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                    {selected.docName}
                  </p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500">
                    {DOC_TYPE_LABELS[selected.type] ?? selected.type} &middot;{" "}
                    {paramEntries.length} parameters
                  </p>
                </div>
              </div>

              {/* Param list — compact, stacked one below another */}
              <div className="flex-1 overflow-y-auto p-5">
                {paramEntries.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    No parameters
                  </p>
                ) : (
                  <div className="max-w-md flex flex-col divide-y divide-gray-100 dark:divide-gray-800">
                    {paramEntries.map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-baseline justify-between gap-3 py-1.5"
                      >
                        <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide shrink-0">
                          {key}
                        </span>
                        <span className="text-xs font-mono text-gray-800 dark:text-gray-200 break-all text-right">
                          {key === "stream"
                            ? String(value)?.slice(0, 50)
                            : String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <FileText
                  size={22}
                  className="text-gray-400 dark:text-gray-500"
                  strokeWidth={1.5}
                />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Select a document
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  Click a document on the left to inspect its parameters
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom action row */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {productionDocList.length}{" "}
          {productionDocList.length === 1 ? "document" : "documents"} ready
        </span>
        <Button
          variant="default"
          size="sm"
          disabled={
            !!signees && signees.length > 0 && selectedSigneeIds.size === 0
          }
          onClick={() =>
            onProduce(
              signees?.filter((s) => selectedSigneeIds.has(s.id)) ?? [],
              orderedList
            )
          }
        >
          {actionLabel}
        </Button>
      </div>
    </div>
  );
};

export const DocProducerContent = ({
  selectedContact,
}: DocProducerContentProps) => {
  const apolloClient = useApolloClient();
  const { state, dispatch } = useDocProducer();
  const [showForm, setShowForm] = useState(false);
  const [activeLoadFormId, setActiveLoadFormId] = useState<number | null>(null);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [showOrderFormNew, setShowOrderFormNew] = useState(false);
  const [editingOrderDoc, setEditingOrderDoc] =
    useState<DocProducerDocument | null>(null);
  const [activeQualityForm, setActiveQualityForm] = useState<
    | "meetingMinutes"
    | "suitability"
    | "suitabilityNew"
    | "kycPriv"
    | "kycOrg"
    | "openCustomerInstruction"
    | "advisoryAgainst"
    | null
  >(null);
  const [showUploader, setShowUploader] = useState(false);
  const [showProduceChoice, setShowProduceChoice] = useState(false);
  const [productionDocList, setProductionDocList] = useState<
    ProductionDocument[]
  >([]);
  const [showProductionList, setShowProductionList] = useState(false);
  const [showSigningList, setShowSigningList] = useState(false);
  const [signingSignees, setSigningSignees] = useState<Signee[]>([]);
  const [isProducing, setIsProducing] = useState(false);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [documentPreview, setDocumentPreview] = useState<string | null>(null);
  const [selectedAttachment, setSelectedAttachment] = useState<string>("");
  const [producedSigningLinks, setProducedSigningLinks] = useState<
    Array<{ name: string; contactId: string; link: string; status: string }>
  >([]);
  const [copiedSigningLink, setCopiedSigningLink] = useState<string | null>(
    null
  );
  const [paramOverrides, setParamOverrides] = useState<Record<string, string>>(
    {}
  );
  const [pendingKundeprofilDocumentId, setPendingKundeprofilDocumentId] =
    useState<string | null>(null);
  const contactId = selectedContact?.id;

  const { linkedContact, userProfile } = useKeycloak();

  const { fetchCompletedTask } = useGetCompletedProcessTask();
  const { fetchProducedDocuments } = useGetProducedDocuments();
  const { downloadAttachment } = useDownloadAttachment();

  const { loading, error, data, refetch, networkStatus } =
    useGetContactOverview(selectedContact?.id);

  const { data: advisorData, loading: advisorLoading } =
    useGetExtendedContactDetails(linkedContact);

  // --------------------------------------------
  // Securities list
  // --------------------------------------------
  const { data: securities, loading: securitiesLoading } =
    useGetSavedQuery2<QuerySecurity>("advPortalInstruments", {
      status: "ACTIVE",
      typeCode: "%",
      currencyCode: "%",
      securityName: "%",
      tag: "%Capguard-Yes%",
    });

  // --------------------------------------------
  // Profile data
  // --------------------------------------------
  const { data: profileData, loading: profileLoading } =
    useGetSavedQuery2<QueryProfile>(
      "advPortalProfile",
      { profileKey: `contact-${contactId}` },
      !contactId
    );

  // --------------------------------------------
  // Model portfolios
  // --------------------------------------------
  const { data: modelsData, loading: modelsLoading } =
    useGetSavedQuery2<QueryModelPosition>("advPortalModel", {});

  const selectedContactState = state.contacts.find((c) => c.id === contactId);
  const persistedOverrides = selectedContactState?.docProducerOverrides;
  const docStamperOverrides = useMemo(
    () => persistedOverrides?.profile ?? {},
    [persistedOverrides?.profile]
  );
  const docStamperSourceDocIds = useMemo(
    () => new Set(persistedOverrides?.sourceDocIds ?? []),
    [persistedOverrides?.sourceDocIds]
  );
  const afterSignOutput = persistedOverrides?.afterSign ?? null;
  const updateDocProducerOverrides = useCallback(
    (patch: Partial<DocProducerOverrides>) => {
      if (!contactId) return;
      const current = selectedContactState?.docProducerOverrides;
      dispatch({
        type: "SET_DOC_PRODUCER_OVERRIDES",
        payload: {
          contactId: String(contactId),
          overrides: {
            profile: patch.profile ?? current?.profile ?? {},
            sourceDocIds: patch.sourceDocIds ?? current?.sourceDocIds ?? [],
            afterSign: patch.afterSign ?? current?.afterSign ?? null,
          },
        },
      });
    },
    [contactId, dispatch, selectedContactState?.docProducerOverrides]
  );
  const isLoading = loading || securitiesLoading;

  // Memoize documents list
  const documents = useMemo(() => {
    return selectedContactState?.documents?.documents || [];
  }, [selectedContactState]);
  const [documentFilter, setDocumentFilter] = useState<
    "all" | "orders" | "other"
  >("all");
  const documentCounts = useMemo(
    () => ({
      all: documents.length,
      orders: documents.filter((doc) => doc.docType === "order").length,
      other: documents.filter((doc) => doc.docType !== "order").length,
    }),
    [documents]
  );
  const visibleDocuments = useMemo(
    () =>
      documentFilter === "all"
        ? documents
        : documents.filter((doc) =>
            documentFilter === "orders"
              ? doc.docType === "order"
              : doc.docType !== "order"
          ),
    [documentFilter, documents]
  );

  const [docData, setDocData] = useState<DocProducerData | null>(null);
  const docDataRef = useRef<DocProducerData | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    docDataRef.current = docData;
  }, [docData]);

  // Reset form when selectedContact changes
  useEffect(() => {
    setShowForm(false);
    setActiveQualityForm(null);
    setActiveLoadFormId(null);
    setShowUploader(false);
    setDocData(null);
    setDocumentPreview(null);
    setSelectedAttachment("");
    setParamOverrides({});
    setPendingKundeprofilDocumentId(null);
    setDocumentFilter("all");
  }, [selectedContact?.id]);

  // Auto-add contact to state when contact data is loaded
  useEffect(() => {
    if (!data && contactId && !loading) {
      refetch();
      return;
    }
    if (data?.contact && contactId) {
      const exists = state.contacts.some((c) => c.id === contactId);
      if (
        !exists &&
        data.contact &&
        data.contact.id === contactId &&
        advisorData?.contact
      ) {
        const newEntry: ContactEntry = {
          id: contactId,
          contactId: String(contactId),
          contact: data.contact,
          documents: {
            documents: [],
            contactData: data.contact,
          },
          modData: {},
        };
        dispatch({ type: "ADD_CONTACT", payload: newEntry });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data,
    advisorData,
    loading,
    networkStatus,
    showForm,
    contactId,
    state.contacts,
    dispatch,
    refetch,
  ]);

  const handleDocumentClick = useCallback(
    (formKey: string, documentTitle: string, isProduction = false) => {
      setShowOrderForm(false);
      setActiveQualityForm(null);
      setDocData({
        formKey,
        title: documentTitle,
        toProduction: isProduction,
      });
      setShowForm(true);
    },
    []
  );

  const replaceContactDocuments = useCallback(
    (documents: {
      documents: DocProducerDocument[];
      [key: string]: object;
    }) => {
      if (contactId) {
        dispatch({
          type: "REPLACE_DOCUMENTS",
          payload: { contactId: String(contactId), documents },
        });
      }
    },
    [contactId, dispatch]
  );

  const fetchAttachmentAsBase64 = useCallback(
    async (taskId: string, attachmentName: string) => {
      try {
        const DOWNLOAD_ATTACHMENT_QUERY = gql`
          query GetTaskAttachment($taskId: String, $attachmentName: String) {
            task(taskId: $taskId) {
              attachment(name: $attachmentName)
            }
          }
        `;

        const { data } = await apolloClient.query({
          query: DOWNLOAD_ATTACHMENT_QUERY,
          variables: {
            taskId,
            attachmentName,
          },
          fetchPolicy: "network-only",
        });

        return data?.task?.attachment || null;
      } catch (error) {
        console.error("Error fetching attachment:", error);
        return null;
      }
    },
    [apolloClient]
  );

  const onFetchTask = useCallback(
    async (taskId: string, title: string, isProduction = false) => {
      const { data, networkStatus } = await fetchCompletedTask(taskId);
      const json = data?.processCompletedTasks.processTask[0];
      if (isProduction) {
        const processInstanceId = json.processInstanceId;
        const { data: prodData } = await fetchProducedDocuments(
          processInstanceId
        );
        if (prodData && prodData.tasks.length > 0) {
          const atts: TaskAttachment[] = [];
          for (const task of prodData.tasks) {
            for (const att of task.attachments) {
              atts.push({ taskId: task.taskId, name: att });
            }
          }
          setAttachments(atts);

          // Auto-select and preview the first PDF attachment
          const firstPdf = atts.find((a) =>
            a.name.toLowerCase().endsWith(".pdf")
          );
          if (firstPdf) {
            setSelectedAttachment(`${firstPdf.taskId}|${firstPdf.name}`);
            const base64Data = await fetchAttachmentAsBase64(
              firstPdf.taskId,
              firstPdf.name
            );
            if (base64Data) {
              setDocumentPreview(base64Data);
              setShowForm(true);
            }
          }
        }
        return;
      }
      // Ensure documents are of type DocProducerDocument
      const returnValues = json?.data?.returnValues;
      if (returnValues?.documents?.length > 0) {
        replaceContactDocuments(returnValues);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rawData = json?.data as any;
      const returnedAfterSign = rawData?.docProdOut?.afterSign;
      const importProfile:
        | Array<{ profKey: string; profVal: unknown }>
        | undefined = rawData?.docProdOut?.afterSign?.importProfile;
      const updateMainContact: Array<Record<string, unknown>> | undefined =
        rawData?.docProdOut?.afterSign?.updateMainContact;

      const profileUpdates = [
        ...(Array.isArray(importProfile)
          ? importProfile
              .filter((e) => e.profKey && e.profVal != null)
              .map((e) => [e.profKey, e.profVal] as const)
          : []),
        ...(Array.isArray(updateMainContact)
          ? updateMainContact.flatMap((contactUpdate) =>
              Object.entries(contactUpdate).filter(
                ([key, value]) => key && value != null
              )
            )
          : []),
      ];

      if (profileUpdates.length > 0) {
        const overrides = Object.fromEntries(
          profileUpdates.map(([key, value]) => [key, String(value)])
        );
        const sourceIds = new Set(
          (returnValues?.documents ?? []).map((d: { id: string }) => d.id)
        );
        updateDocProducerOverrides({
          profile: overrides,
          sourceDocIds: Array.from(sourceIds),
          ...(returnedAfterSign &&
          typeof returnedAfterSign === "object" &&
          !Array.isArray(returnedAfterSign)
            ? { afterSign: returnedAfterSign as Record<string, unknown> }
            : {}),
        });
      } else if (
        returnedAfterSign &&
        typeof returnedAfterSign === "object" &&
        !Array.isArray(returnedAfterSign)
      ) {
        updateDocProducerOverrides({
          afterSign: returnedAfterSign as Record<string, unknown>,
        });
      }

      // Documents returned from Kundeprofil/MIFID/KYC forms that should be
      // added to the production list, filled with the shared docStamperData.
      const signDetails = rawData?.docProdOut?.signDetails as
        | {
            documentsFromSecurity?: Array<{
              documentType: string;
              fileName: string;
            }>;
            docStamperData?: Record<string, unknown>;
          }
        | undefined;
      const documentsFromSecurity = signDetails?.documentsFromSecurity ?? [];
      if (documentsFromSecurity.length > 0 && contactId) {
        const docStamperData = signDetails?.docStamperData ?? {};

        // Remove the optimistic "kundeprofil" placeholder now that the real
        // produced documents have arrived — avoids showing both.
        for (const placeholder of documents.filter(
          (d) => d.docType === "kundeprofil"
        )) {
          dispatch({
            type: "REMOVE_DOCUMENT",
            payload: { contactId: String(contactId), taskId: placeholder.id },
          });
        }

        for (const item of documentsFromSecurity) {
          dispatch({
            type: "ADD_DOCUMENT",
            payload: {
              contactId: String(contactId),
              document: {
                id: `docFromSecurity_${
                  item.fileName
                }_${new Date().toISOString()}`,
                docType: "documentFromSecurity",
                processName: item.documentType,
                desc: item.fileName,
                data: {
                  documentType: item.documentType,
                  fileName: item.fileName,
                  docStamperData,
                },
              },
            },
          });
        }
      }
    },
    [
      fetchCompletedTask,
      fetchProducedDocuments,
      replaceContactDocuments,
      fetchAttachmentAsBase64,
      contactId,
      dispatch,
      documents,
      updateDocProducerOverrides,
    ]
  );

  const handleModalClose = useCallback(
    async (taskId: string, title: string, isProduction = false) => {
      await onFetchTask(taskId, title, isProduction);
      setDocData(null);
      if (!isProduction) {
        setShowForm(false);
        setDocumentPreview(null);
      }
    },
    [onFetchTask]
  );

  const handleComplete = useCallback(
    (taskId: string) => {
      const currentDocData = docDataRef.current;
      if (currentDocData) {
        handleModalClose(
          taskId,
          currentDocData.title,
          currentDocData.toProduction
        );
      }
    },
    [handleModalClose]
  );

  // Runs the production process imperatively — no form UI shown.
  // A unique referenceId is embedded in the payload; the Flowable process stores
  // it as the task reference so we can find exactly that task afterwards.
  // isSigning=true adds signees to the payload for the signing flow.
  // docList is the ordered list from ProductionListView (after user drag-and-drop reordering).

  // Contact name used as part of the signing task filter
  const contactName = data?.contact?.name ?? "";

  const handleStartProcess = useCallback(
    async (
      isSigning: boolean,
      signees: Signee[],
      docList?: ProductionDocument[]
    ) => {
      if (isProducing) return;
      setIsProducing(true);
      setShowProductionList(false);
      setShowSigningList(false);
      try {
        const referenceId = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
          /[xy]/g,
          (c) => {
            const r = crypto.getRandomValues(new Uint8Array(1))[0] & 15;
            return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
          }
        );

        // Fire the production process — returns null (no user task/form defined)
        await apolloClient.mutate({
          mutation: PRODUCTION_START_PROCESS,
          variables: {
            key: "dp_documentProduction",
            data: {
              productionDocList: docList ?? productionDocList,
              referenceId,
              isSigning,
              selectedContactId: contactId,
              afterSign: afterSignOutput,
              ...(isSigning ? { signees } : {}),
            },
          },
        });

        // Fetch produced documents.
        // For signing: filter by "Portal-docProd for <contactName>", then pick the
        // most recent task with a createDate within 30 minutes of now.
        // For production: filter by the unique referenceId.
        const MAX_ATTEMPTS = 5;
        const INITIAL_DELAY_MS = 2000;
        const RETRY_DELAY_MS = 1500;

        const taskFilter = isSigning
          ? `Portal-docProd for ${contactName}`
          : referenceId;

        await new Promise((r) => setTimeout(r, INITIAL_DELAY_MS));

        let targetTask: ProducedDocumentsTask["tasks"][0] | undefined;
        let prodData: ProducedDocumentsTask | undefined = undefined;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
          const result = await fetchProducedDocuments(taskFilter, isSigning);
          const tasks = result.data?.tasks ?? [];

          if (isSigning) {
            // Pick the newest task created within the last 30 minutes
            const now = dayjs();
            const recent = tasks
              .filter((t) => {
                const d = dayjs(t.createDate);
                return d.isValid() && now.diff(d, "minute") <= 2;
              })
              .sort(
                (a, b) =>
                  dayjs(b.createDate).valueOf() - dayjs(a.createDate).valueOf()
              );
            if (recent.length > 0) {
              targetTask = recent[0];
              prodData = { tasks: recent };
              break;
            }
          } else {
            if (tasks.length > 0) {
              prodData = result.data;
              break;
            }
          }

          if (attempt < MAX_ATTEMPTS) {
            await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
          }
        }

        if (!prodData || prodData.tasks.length === 0) {
          toast.error("Kunne ikke hente produserte dokumenter");
          return;
        }

        // For signing: parse signing links from the target task description
        if (isSigning && targetTask) {
          setProducedSigningLinks(parseSigningInfo(targetTask.description));
        }

        // Build attachment list — for signing use only the target task
        const taskList =
          isSigning && targetTask ? [targetTask] : prodData.tasks;
        const atts: TaskAttachment[] = [];
        for (const task of taskList) {
          for (const att of task.attachments) {
            atts.push({ taskId: task.taskId, name: att });
          }
        }
        setAttachments(atts);

        const firstPdf = atts.find((a) =>
          a.name.toLowerCase().endsWith(".pdf")
        );
        if (firstPdf) {
          setSelectedAttachment(`${firstPdf.taskId}|${firstPdf.name}`);
          const base64Data = await fetchAttachmentAsBase64(
            firstPdf.taskId,
            firstPdf.name
          );
          if (base64Data) {
            setDocumentPreview(base64Data);
            setShowForm(true);
          }
        }
      } catch (err) {
        console.error("Production error:", err);
        toast.error("Produksjon feilet. Prøv igjen.");
      } finally {
        setIsProducing(false);
      }
    },
    [
      apolloClient,
      isProducing,
      productionDocList,
      contactId,
      contactName,
      fetchProducedDocuments,
      fetchAttachmentAsBase64,
      afterSignOutput,
    ]
  );

  // Memoize contact values to prevent re-renders
  const contactValues = useMemo(
    () => state.contacts.find((c) => c.id === contactId),
    [state.contacts, contactId]
  );

  const handleCopyFormDocumentsJson = useCallback(async () => {
    const contact = contactValues?.contact;
    if (!contact) return;
    const json = JSON.stringify(
      buildFormDocumentList(contact, documents),
      null,
      2
    );
    try {
      await navigator.clipboard.writeText(json);
      toast.success("Dokument-JSON kopiert til utklippstavlen", {
        autoClose: 700,
        hideProgressBar: false,
      });
    } catch {
      toast.error("Kunne ikke kopiere til utklippstavlen", {
        autoClose: 700,
        hideProgressBar: false,
      });
    }
  }, [contactValues, documents]);

  const isCompany = contactValues?.contact?.juridical?.code === "Company";
  const getKundeprofilUpdateType = (document: DocProducerDocument) => {
    const documentData = document.data as Record<string, unknown>;
    const updateType = documentData.updateType;
    if (updateType === "1" || updateType === "2" || updateType === "3") {
      return updateType;
    }

    const searchableText =
      `${document.docType} ${document.processName} ${document.desc}`.toLowerCase();
    if (
      document.docType === "MIFID" ||
      searchableText.includes("mifid") ||
      searchableText.includes("investorprofil")
    ) {
      return "2";
    }
    if (document.docType === "KYC" || searchableText.includes("aml")) {
      return "3";
    }
    if (
      document.docType === "kundeprofil" ||
      searchableText.includes("kundeprofil")
    ) {
      return "1";
    }
    return null;
  };
  const hasSuitabilityDocument = documents.some(
    (document) =>
      document.docType === "docSuitabilityNew" ||
      document.docType === "suitabilityStatement"
  );
  const hasMifid = documents.some(
    (document) => getKundeprofilUpdateType(document) === "2"
  );
  const hasKyc = documents.some(
    (document) => getKundeprofilUpdateType(document) === "3"
  );
  const hasKundeprofilDocument = documents.some(
    (document) => getKundeprofilUpdateType(document) === "1"
  );

  const handleDeleteDocument = useCallback(
    (document: DocProducerDocument) => {
      if (contactId) {
        dispatch({
          type: "REMOVE_DOCUMENT",
          payload: { contactId: String(contactId), taskId: document.id },
        });
        const clearsOverrides =
          document.docType === "kundeprofil" ||
          document.docType === "documentFromSecurity" ||
          docStamperSourceDocIds.has(document.id);
        if (clearsOverrides && Object.keys(docStamperOverrides).length > 0) {
          updateDocProducerOverrides({ profile: {}, sourceDocIds: [] });
        }
      }
    },
    [
      contactId,
      dispatch,
      docStamperOverrides,
      docStamperSourceDocIds,
      updateDocProducerOverrides,
    ]
  );

  const handleEditOrderDoc = useCallback((doc: DocProducerDocument) => {
    setEditingOrderDoc(doc);
    setShowOrderFormNew(true);
    setShowForm(true);
  }, []);

  // Pending orders derived from the order documents in the list
  const pendingOrders = useMemo<PendingOrder[]>(() => {
    return documents
      .filter((d) => d.docType === "order")
      .map((d) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const nd = ((d.data as any).newData ?? {}) as Record<string, unknown>;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ed = ((d.data as any).existData ?? {}) as Record<string, unknown>;
        const modelType = String(nd.dpModelType ?? "");
        const buyRowsForNames =
          (nd.buyOrders as Array<{ instrumentName?: string }>) ?? [];
        const sellRowsForNames =
          (nd.sellOrders as Array<{
            name?: string;
            instrumentName?: string;
          }>) ?? [];
        const instrumentNames = Array.from(
          new Set(
            [
              ...buyRowsForNames.map((r) => (r.instrumentName ?? "").trim()),
              ...sellRowsForNames.map((r) =>
                (r.instrumentName ?? r.name ?? "").trim()
              ),
            ].filter(Boolean)
          )
        );
        const manualOrderRows = buyRowsForNames
          .map((r) => {
            const instrumentCode = String(
              (r as { instrumentCode?: string }).instrumentCode ?? ""
            ).trim();
            const name = (
              r.instrumentName ??
              (r as { instrumentCode?: string }).instrumentCode ??
              ""
            ).trim();
            const amountNok = parseOrderAmount(
              (r as { amountInput?: string | number }).amountInput
            );
            const class1 = String(
              (r as { class1code?: string }).class1code ?? ""
            ).toUpperCase();
            const class2 = String(
              (r as { class2code?: string }).class2code ?? ""
            ).toUpperCase();
            const isStock = class1 === "E" || class2 === "S" || class2 === "EF";
            const security = findSecurityForOrderRow(
              securities,
              instrumentCode,
              name
            );
            return { instrumentName: name, amountNok, isStock, security };
          })
          .filter((r) => r.instrumentName && r.amountNok > 0);

        // For NOMODEL orders, compute stock fraction from the actual buy order lines.
        // A buy row is "stock" if class1code === "E" (equities) OR class2code is "S"/"EF"
        // (same logic as buildSuitOrder.ts resolveIsStock).
        let stockFraction: number | undefined;
        if (!modelType || modelType === "NOMODEL") {
          const buyRows =
            (nd.buyOrders as
              | Array<{
                  instrumentName?: string;
                  class1code?: string;
                  class2code?: string;
                  amountInput?: string;
                }>
              | undefined) ?? [];
          const filledRows = buyRows.filter(
            (r) =>
              r.instrumentName ||
              (r as { instrumentCode?: string }).instrumentCode
          );
          const totalBuy = filledRows.reduce(
            (s, r) => s + parseOrderAmount(r.amountInput),
            0
          );
          if (totalBuy > 0) {
            const stockBuy = filledRows
              .filter(
                (r) =>
                  String(r.class1code ?? "").toUpperCase() === "E" ||
                  String(r.class2code ?? "").toUpperCase() === "S" ||
                  String(r.class2code ?? "").toUpperCase() === "EF"
              )
              .reduce((s, r) => s + parseOrderAmount(r.amountInput), 0);
            stockFraction = stockBuy / totalBuy;
          } else {
            stockFraction = 0;
          }
        }

        return {
          portfolioId: String(nd.dpPortfolioId ?? ""),
          portfolioName: String(
            nd.dpPortfolioName ?? ed.dpPortfolioName ?? "Portefolje"
          ),
          portfolioType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
          isNew: !(ed.isExisting as boolean),
          portType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
          amountToInvest: Number(nd.dpAmountToInvest ?? 0),
          oneOffAmount: Number(nd.dpOnOffAmount ?? 0),
          sumAvCash: Number(ed.dpSumAvCash ?? 0),
          sumInsValue: Number(ed.dpSumInsValue ?? 0),
          withdrawal: Number(nd.dpWithdrawal ?? 0),
          modelType,
          modelTypeName: String(nd.dpModelTypeName ?? ""),
          instrumentNames,
          manualOrderRows,
          managementFeePct: Number(nd.dpManagementFee ?? 0),
          subscriptionFeePct: Number(nd.dpOnOffPercentage ?? 0),
          feeType: String(nd.dpFeeType ?? ""),
          stockFraction,
        };
      });
  }, [documents, securities]);

  const handleDownloadAttachment = useCallback(
    async (taskId: string, attachmentName: string) => {
      try {
        const base64Data = await fetchAttachmentAsBase64(
          taskId,
          attachmentName
        );
        if (base64Data) {
          // Extract base64 content (remove data:application/pdf;base64, prefix if present)
          const base64Content = base64Data.includes(",")
            ? base64Data.split(",")[1]
            : base64Data;

          // Convert base64 to blob
          const byteCharacters = atob(base64Content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });

          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = attachmentName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        console.error("Error downloading attachment:", error);
        toast.error("Could not download the document. Please try again.");
      }
    },
    [fetchAttachmentAsBase64]
  );

  const formValues: ContactEntry | undefined = useMemo(() => {
    if (
      !contactId ||
      !contactValues ||
      !securities ||
      !profileData ||
      !modelsData ||
      !advisorData
    )
      return undefined;
    return {
      id: contactId,
      contact: contactValues.contact,
      contactId: String(contactId),
      documents: {
        ...contactValues?.documents,
        avdData: advisorData.contact,
        faSec: securities,
        profile: buildProfileMap(profileData),
        faModels: modelsData,
      },
      modData: {},
    };
  }, [
    contactId,
    contactValues,
    securities,
    profileData,
    modelsData,
    advisorData,
  ]);

  // Effective profile map for SuitStatement: profileMap merged with user overrides
  const baseProfileMap = useMemo(
    () => buildProfileMap(profileData ?? []),
    [profileData]
  );
  const effectiveProfileMap = useMemo(
    () => ({ ...baseProfileMap, ...paramOverrides, ...docStamperOverrides }),
    [baseProfileMap, paramOverrides, docStamperOverrides]
  );
  // docStamperOverrides win over manual test-param overrides
  const effectiveParamOverrides = useMemo(
    () => ({ ...paramOverrides, ...docStamperOverrides }),
    [paramOverrides, docStamperOverrides]
  );
  const handleParamOverrideChange = useCallback(
    (key: string, value: string) =>
      setParamOverrides((prev) => {
        if (value === "") {
          const next = { ...prev };
          delete next[key];
          return next;
        }
        return { ...prev, [key]: value };
      }),
    []
  );

  // Memoize FormView to prevent re-renders when dialog moves
  const formView = useMemo(() => {
    if (!showForm || !contactId) return null;

    // Meeting Minutes form
    if (activeQualityForm === "meetingMinutes") {
      return (
        <div className="h-full overflow-auto px-4 pt-4">
          <MeetingMinutesForm
            contactName={data?.contact?.name ?? undefined}
            advisorName={advisorData?.contact?.name ?? undefined}
            onDocumentCreated={(data) => {
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: `meeting_${new Date().toISOString()}`,
                    docType: "meetingMinutes",
                    processName: "Meeting Minutes",
                    desc: "Meeting Minutes",
                    data: {
                      newData: data,
                    },
                  },
                },
              });
            }}
            onClose={() => {
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // Suitability form
    if (activeQualityForm === "suitability") {
      const activePortfolios = (
        contactValues?.contact?.portfolios ?? []
      ).filter((p) => p.status === "A");

      const pendingOrders: PendingOrder[] = documents
        .filter((d) => d.docType === "order")
        .map((d) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const nd = ((d.data as any).newData ?? {}) as Record<string, unknown>;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const ed = ((d.data as any).existData ?? {}) as Record<
            string,
            unknown
          >;
          const modelType = String(nd.dpModelType ?? "");
          const buyRows =
            (nd.buyOrders as
              | Array<{
                  instrumentName?: string;
                  instrumentCode?: string;
                  class1code?: string;
                  class2code?: string;
                  amountInput?: string | number;
                }>
              | undefined) ?? [];
          const sellRows =
            (nd.sellOrders as Array<{
              name?: string;
              instrumentName?: string;
            }>) ?? [];
          const instrumentNames = Array.from(
            new Set(
              [
                ...buyRows.map((r) => (r.instrumentName ?? "").trim()),
                ...sellRows.map((r) =>
                  (r.instrumentName ?? r.name ?? "").trim()
                ),
              ].filter(Boolean)
            )
          );
          const manualOrderRows = buyRows
            .map((r) => {
              const instrumentCode = String(
                (r as { instrumentCode?: string }).instrumentCode ?? ""
              ).trim();
              const name = (
                r.instrumentName ??
                (r as { instrumentCode?: string }).instrumentCode ??
                ""
              ).trim();
              const amountNok = parseOrderAmount(
                (r as { amountInput?: string | number }).amountInput
              );
              const class1 = String(
                (r as { class1code?: string }).class1code ?? ""
              ).toUpperCase();
              const class2 = String(
                (r as { class2code?: string }).class2code ?? ""
              ).toUpperCase();
              const isStock =
                class1 === "E" || class2 === "S" || class2 === "EF";
              const security = findSecurityForOrderRow(
                securities,
                instrumentCode,
                name
              );
              return { instrumentName: name, amountNok, isStock, security };
            })
            .filter((r) => r.instrumentName && r.amountNok > 0);
          const stockFraction =
            !modelType || modelType === "NOMODEL"
              ? computeNoModelStockFraction(buyRows)
              : undefined;
          return {
            portfolioId: String(nd.dpPortfolioId ?? ""),
            portfolioName: String(
              nd.dpPortfolioName ?? ed.dpPortfolioName ?? "Portefolje"
            ),
            portfolioType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
            isNew: !(ed.isExisting as boolean),
            portType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
            amountToInvest: Number(nd.dpAmountToInvest ?? 0),
            oneOffAmount: Number(nd.dpOnOffAmount ?? 0),
            sumAvCash: Number(ed.dpSumAvCash ?? 0),
            sumInsValue: Number(ed.dpSumInsValue ?? 0),
            withdrawal: Number(nd.dpWithdrawal ?? 0),
            modelType,
            modelTypeName: String(nd.dpModelTypeName ?? ""),
            instrumentNames,
            manualOrderRows,
            managementFeePct: Number(nd.dpManagementFee ?? 0),
            subscriptionFeePct: Number(nd.dpOnOffPercentage ?? 0),
            feeType: String(nd.dpFeeType ?? ""),
            stockFraction,
          };
        });

      return (
        <div className="h-full overflow-auto p-4">
          <SuitabilityForm
            profileData={profileData ?? []}
            portfolios={activePortfolios}
            pendingOrders={pendingOrders}
            modelsData={modelsData ?? []}
            onDocumentCreated={(data) => {
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: `suitability_${new Date().toISOString()}`,
                    docType: "suitabilityStatement",
                    processName: "Suitability",
                    desc: "Suitability",
                    data: {
                      newData: data,
                      existData: {
                        isExisting: false,
                      },
                    },
                  },
                },
              });
            }}
            onClose={() => {
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // Suitability New form (with docType: docSuitabilityNew)
    if (activeQualityForm === "suitabilityNew") {
      const activePortfolios = (
        contactValues?.contact?.portfolios ?? []
      ).filter((p) => p.status === "A");

      const pendingOrders: PendingOrder[] = documents
        .filter((d) => d.docType === "order")
        .map((d) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const nd = ((d.data as any).newData ?? {}) as Record<string, unknown>;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const ed = ((d.data as any).existData ?? {}) as Record<
            string,
            unknown
          >;
          const modelType = String(nd.dpModelType ?? "");
          const buyRows =
            (nd.buyOrders as
              | Array<{
                  instrumentName?: string;
                  instrumentCode?: string;
                  class1code?: string;
                  class2code?: string;
                  amountInput?: string | number;
                }>
              | undefined) ?? [];
          const sellRows =
            (nd.sellOrders as Array<{
              name?: string;
              instrumentName?: string;
            }>) ?? [];
          const instrumentNames = Array.from(
            new Set(
              [
                ...buyRows.map((r) => (r.instrumentName ?? "").trim()),
                ...sellRows.map((r) =>
                  (r.instrumentName ?? r.name ?? "").trim()
                ),
              ].filter(Boolean)
            )
          );
          const manualOrderRows = buyRows
            .map((r) => {
              const instrumentCode = String(
                (r as { instrumentCode?: string }).instrumentCode ?? ""
              ).trim();
              const name = (
                r.instrumentName ??
                (r as { instrumentCode?: string }).instrumentCode ??
                ""
              ).trim();
              const amountNok = parseOrderAmount(
                (r as { amountInput?: string | number }).amountInput
              );
              const class1 = String(
                (r as { class1code?: string }).class1code ?? ""
              ).toUpperCase();
              const class2 = String(
                (r as { class2code?: string }).class2code ?? ""
              ).toUpperCase();
              const isStock =
                class1 === "E" || class2 === "S" || class2 === "EF";
              const security = findSecurityForOrderRow(
                securities,
                instrumentCode,
                name
              );
              return { instrumentName: name, amountNok, isStock, security };
            })
            .filter((r) => r.instrumentName && r.amountNok > 0);
          const stockFraction =
            !modelType || modelType === "NOMODEL"
              ? computeNoModelStockFraction(buyRows)
              : undefined;
          return {
            portfolioId: String(nd.dpPortfolioId ?? ""),
            portfolioName: String(
              nd.dpPortfolioName ?? ed.dpPortfolioName ?? "Portefolje"
            ),
            portfolioType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
            isNew: !(ed.isExisting as boolean),
            portType: String(nd.dpPortType ?? ed.dpPortType ?? ""),
            amountToInvest: Number(nd.dpAmountToInvest ?? 0),
            oneOffAmount: Number(nd.dpOnOffAmount ?? 0),
            sumAvCash: Number(ed.dpSumAvCash ?? 0),
            sumInsValue: Number(ed.dpSumInsValue ?? 0),
            withdrawal: Number(nd.dpWithdrawal ?? 0),
            modelType,
            modelTypeName: String(nd.dpModelTypeName ?? ""),
            instrumentNames,
            manualOrderRows,
            managementFeePct: Number(nd.dpManagementFee ?? 0),
            subscriptionFeePct: Number(nd.dpOnOffPercentage ?? 0),
            feeType: String(nd.dpFeeType ?? ""),
            stockFraction,
          };
        });

      if (!contactValues?.contact) return null;

      return (
        <div className="h-full overflow-auto p-4">
          <SuitabilityNewForm
            contact={contactValues.contact}
            profileData={profileData ?? []}
            portfolios={activePortfolios}
            pendingOrders={pendingOrders}
            modelsData={modelsData ?? []}
            paramOverrides={effectiveParamOverrides}
            onDocumentCreated={(suitData) => {
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: `suitabilityNew_${new Date().toISOString()}`,
                    docType: "docSuitabilityNew",
                    processName: "Suitability (NEW)",
                    desc: "Suitability (NEW)",
                    data: {
                      newData: suitData,
                      existData: {
                        isExisting: false,
                      },
                    },
                  },
                },
              });
            }}
            onClose={() => {
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // KYC Privat form
    if (activeQualityForm === "kycPriv") {
      return (
        <div className="h-full overflow-auto p-4">
          <KycPrivForm
            contact={data?.contact}
            profileData={profileData ?? []}
            onDocumentCreated={(data) => {
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: `kycPriv_${new Date().toISOString()}`,
                    docType: "customerAgreement",
                    processName: "KYC Privat",
                    desc: "KYC Privat",
                    data,
                  },
                },
              });
            }}
            onClose={() => {
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // KYC Org form
    if (activeQualityForm === "kycOrg") {
      return (
        <div className="h-full overflow-auto p-4">
          <KycOrgForm
            contact={data?.contact}
            profileData={profileData ?? []}
            primaryAddress={data?.contact?.addresses?.[0]}
            onDocumentCreated={(data) => {
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: `kycOrg_${new Date().toISOString()}`,
                    docType: "customerAgreement",
                    processName: "KYC Org",
                    desc: "KYC Org",
                    data,
                  },
                },
              });
            }}
            onClose={() => {
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // Open Customer Instruction form
    if (activeQualityForm === "openCustomerInstruction") {
      return (
        <div className="h-full overflow-auto p-4">
          <OpenCustomerInstructionForm
            onDocumentCreated={(data) => {
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: `openCustomerInstruction_${new Date().toISOString()}`,
                    docType: "openCustomerInstruction",
                    processName: "Open customer instruction",
                    desc: "Open customer instruction",
                    data: {
                      newData: data,
                    },
                  },
                },
              });
            }}
            onClose={() => {
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // Advisory Against form
    if (activeQualityForm === "advisoryAgainst") {
      return (
        <div className="h-full overflow-auto p-4">
          <AdvisoryAgainstForm
            onDocumentCreated={(data) => {
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: `advisoryAgainst_${new Date().toISOString()}`,
                    docType: "advisoryAgainst",
                    processName: "Advisory against",
                    desc: "Advisory against",
                    data: {
                      newData: data,
                    },
                  },
                },
              });
            }}
            onClose={() => {
              setActiveQualityForm(null);
              setShowForm(false);
            }}
          />
        </div>
      );
    }

    // OrderFormNew
    if (showOrderFormNew && contactValues) {
      const portfolios = contactValues.contact?.portfolios ?? [];
      const editingData = editingOrderDoc
        ? ((editingOrderDoc.data as Record<string, unknown>)?.newData as Record<
            string,
            unknown
          >)
        : undefined;

      return (
        <div className="h-full overflow-auto px-4 pt-4">
          <OrderFormNew
            portfolios={portfolios}
            isCompany={isCompany}
            securities={securities || []}
            modelsData={modelsData || []}
            initialData={editingData}
            documentId={editingOrderDoc?.id}
            onDocumentCreated={(data, docId) => {
              dispatch({
                type: "ADD_DOCUMENT",
                payload: {
                  contactId: String(contactId),
                  document: {
                    id: docId ?? `order_${new Date().toISOString()}`,
                    docType: "order",
                    processName: "Order",
                    desc:
                      ((data.newData as Record<string, unknown>)
                        ?.dpPortfolioName as string) || "Order",
                    data,
                  },
                },
              });
            }}
            onClose={() => {
              setShowOrderFormNew(false);
              setShowForm(false);
              setEditingOrderDoc(null);
            }}
          />
        </div>
      );
    }

    // Custom OrderForm
    if (showOrderForm && contactValues) {
      const portfolios = contactValues.contact?.portfolios ?? [];
      const editingData = editingOrderDoc
        ? ((editingOrderDoc.data as Record<string, unknown>)?.newData as Record<
            string,
            unknown
          >)
        : undefined;

      return (
        <div className="h-full overflow-auto px-4 pt-4">
          <OrderForm
            portfolios={portfolios}
            isCompany={isCompany}
            securities={securities || []}
            initialData={editingData}
            documentId={editingOrderDoc?.id}
            onDocumentCreated={(data, documentId) => {
              if (documentId) {
                // Replace the existing document (same id)
                dispatch({
                  type: "ADD_DOCUMENT",
                  payload: {
                    contactId: String(contactId),
                    document: {
                      id: documentId,
                      docType: "order",
                      processName: "Order",
                      desc:
                        ((data.newData as Record<string, unknown>)
                          ?.dpPortfolioName as string) || "Order",
                      data,
                    },
                  },
                });
              } else {
                dispatch({
                  type: "ADD_DOCUMENT",
                  payload: {
                    contactId: String(contactId),
                    document: {
                      id: `order_${new Date().toISOString()}`,
                      docType: "order",
                      processName: "Order",
                      desc:
                        ((data.newData as Record<string, unknown>)
                          ?.dpPortfolioName as string) || "Order",
                      data,
                    },
                  },
                });
              }
            }}
            onClose={() => {
              setShowOrderForm(false);
              setShowForm(false);
              setEditingOrderDoc(null);
            }}
          />
        </div>
      );
    }

    // Generic Formio form loaded via useLoadForm — Order or any future form type
    if (activeLoadFormId !== null && contactValues) {
      return (
        <LoadFormRunner
          profileData={profileData || []}
          formId={activeLoadFormId}
          contactValues={contactValues}
          securities={securities || []}
          onDocumentCreated={(doc) => {
            dispatch({
              type: "ADD_DOCUMENT",
              payload: { contactId: String(contactId), document: doc },
            });
          }}
          onClose={() => {
            setActiveLoadFormId(null);
            setShowForm(false);
          }}
        />
      );
    }

    // If we have a document preview (production mode), show it with side-by-side layout
    if (documentPreview) {
      const allDownloadable = attachments.length > 0;
      return (
        <div className="h-full flex min-h-0">
          {/* Left sidebar — attachment list */}
          <div className="w-64 flex-shrink-0 flex flex-col border-r border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
            {/* Sidebar header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Produced Documents
                </span>
                {attachments.length > 0 && (
                  <span className="text-[10px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full px-1.5 py-0.5 leading-none">
                    {attachments.length}
                  </span>
                )}
              </div>
              {allDownloadable && (
                <button
                  title="Download all"
                  onClick={() =>
                    attachments.forEach((a) =>
                      handleDownloadAttachment(a.taskId, a.name)
                    )
                  }
                  className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 rounded transition-colors"
                >
                  <Download size={14} />
                </button>
              )}
            </div>

            {/* Attachment list */}
            <div className="flex-1 overflow-y-auto pb-2">
              {/* Signing links — shown after signing flow */}
              {producedSigningLinks.length > 0 && (
                <div className="border-b border-gray-200 dark:border-gray-700 mb-1">
                  <div className="px-4 py-2 flex items-center gap-2 bg-blue-50/50 dark:bg-blue-950/20">
                    <ExternalLink
                      size={12}
                      className="text-blue-600 dark:text-blue-400 flex-shrink-0"
                    />
                    <span className="text-[11px] font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide">
                      Signing Links
                    </span>
                  </div>
                  <div className="px-3 py-2 space-y-2">
                    {producedSigningLinks.map((signer, idx) => (
                      <div
                        key={idx}
                        className="p-2.5 rounded-lg bg-background border border-border/50"
                      >
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="min-w-0">
                            <div className="text-xs font-medium truncate">
                              {signer.name}
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {signer.contactId}
                            </div>
                          </div>
                          <span className="flex-shrink-0 text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                            {signer.status}
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(signer.link);
                            setCopiedSigningLink(String(idx));
                            setTimeout(() => setCopiedSigningLink(null), 2000);
                          }}
                          className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded bg-muted/50 hover:bg-muted transition-colors text-left group"
                        >
                          <span className="text-[10px] text-muted-foreground truncate flex-1 font-mono">
                            {signer.link}
                          </span>
                          {copiedSigningLink === String(idx) ? (
                            <Check
                              size={11}
                              className="text-green-600 flex-shrink-0"
                            />
                          ) : (
                            <Copy
                              size={11}
                              className="text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0"
                            />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {attachments.map((att, idx) => {
                const isPdf = att.name.toLowerCase().endsWith(".pdf");
                const isSelected =
                  selectedAttachment === `${att.taskId}|${att.name}`;
                const ext = att.name.split(".").pop()?.toUpperCase() ?? "FILE";

                return (
                  <div
                    key={idx}
                    role="button"
                    tabIndex={isPdf ? 0 : -1}
                    onClick={() =>
                      isPdf &&
                      handleSelectAttachment(`${att.taskId}|${att.name}`)
                    }
                    onKeyDown={(e) =>
                      e.key === "Enter" &&
                      isPdf &&
                      handleSelectAttachment(`${att.taskId}|${att.name}`)
                    }
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 text-left border-l-2 transition-colors group",
                      isSelected
                        ? "border-l-primary bg-primary/5 dark:bg-primary/10"
                        : "border-l-transparent hover:bg-gray-100 dark:hover:bg-gray-800",
                      isPdf ? "cursor-pointer" : "opacity-50 cursor-default"
                    )}
                  >
                    {/* File-type badge */}
                    <div
                      className={cn(
                        "w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 text-[9px] font-bold tracking-tight",
                        isPdf
                          ? "bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 ring-1 ring-red-200 dark:ring-red-800"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 ring-1 ring-gray-200 dark:ring-gray-700"
                      )}
                    >
                      {ext}
                    </div>

                    {/* Name */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate leading-tight">
                        {att.name}
                      </p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">
                        {isPdf ? "Click to preview" : "Not previewable"}
                      </p>
                    </div>

                    {/* Download button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadAttachment(att.taskId, att.name);
                      }}
                      className="flex-shrink-0 p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                      title={`Download ${att.name}`}
                    >
                      <Download size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right — PDF viewer */}
          <div className="flex-1 min-w-0 p-4">
            {selectedAttachment ? (
              <PDFViewer documentPreview={documentPreview} />
            ) : (
              <div className="h-full flex flex-col items-center justify-center gap-3 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <FileText
                    size={22}
                    className="text-gray-400 dark:text-gray-500"
                    strokeWidth={1.5}
                  />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select a document
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    Click a PDF on the left to preview
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Otherwise show the form
    if (!docData || !contactValues || !formValues) return null;

    const viewValues = docData.toProduction
      ? { productionDocList }
      : { ...formValues, ...docData.extraValues };

    return (
      <FormView
        values={viewValues}
        formKey={docData.formKey}
        header={docData.title}
        onComplete={handleComplete}
        inpData={docData.inpData}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showForm,
    showOrderFormNew,
    activeLoadFormId,
    docData,
    contactId,
    contactValues,
    documentPreview,
    attachments,
    selectedAttachment,
    producedSigningLinks,
    copiedSigningLink,
    dispatch,
    productionDocList,
    editingOrderDoc,
    modelsData,
  ]);

  const handleCancelForm = useCallback(() => {
    if (contactId && pendingKundeprofilDocumentId) {
      dispatch({
        type: "REMOVE_DOCUMENT",
        payload: {
          contactId: String(contactId),
          taskId: pendingKundeprofilDocumentId,
        },
      });
    }
    setShowForm(false);
    setShowOrderForm(false);
    setShowOrderFormNew(false);
    setActiveQualityForm(null);
    setActiveLoadFormId(null);
    setShowUploader(false);
    setShowProduceChoice(false);
    setDocData(null);
    setDocumentPreview(null);
    setSelectedAttachment("");
    setProductionDocList([]);
    setShowProductionList(false);
    setShowSigningList(false);
    setSigningSignees([]);
    setEditingOrderDoc(null);
    setIsProducing(false);
    setProducedSigningLinks([]);
    setCopiedSigningLink(null);
    setPendingKundeprofilDocumentId(null);
  }, [contactId, dispatch, pendingKundeprofilDocumentId]);

  const handleUploadClick = useCallback(() => {
    setShowUploader(true);
    setShowForm(false);
    setDocData(null);
  }, []);

  const handleSubmitUploadedDocuments = useCallback(
    (files: UploadedDocument[]) => {
      if (contactId) {
        files.forEach((fileObj) => {
          const newDoc: DocProducerDocument = {
            id: fileObj.file.name + "_" + new Date().toISOString(),
            docType: "uploaded",
            processName: "Uploaded document",
            desc: fileObj.file.name,
            data: fileObj,
          };
          dispatch({
            type: "ADD_DOCUMENT",
            payload: { contactId: String(contactId), document: newDoc },
          });
        });
      }
      setShowUploader(false);
    },
    [contactId, dispatch]
  );

  const { access } = useKeycloak();
  const isAdmin = access.isAdmin;

  const handleSelectAttachment = useCallback(
    async (value: string) => {
      if (!value) return;

      setSelectedAttachment(value);
      const [taskId, attachmentName] = value.split("|");

      // Only allow PDF preview
      if (!attachmentName.toLowerCase().endsWith(".pdf")) {
        toast.error("Only PDF files can be previewed");
        setSelectedAttachment("");
        return;
      }

      const base64Data = await fetchAttachmentAsBase64(taskId, attachmentName);

      if (base64Data) {
        setDocumentPreview(base64Data);
        setShowForm(true);
      }
    },
    [fetchAttachmentAsBase64]
  );

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header with document type buttons */}
      <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-b dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              setEditingOrderDoc(null);
              setShowOrderFormNew(true);
              setShowForm(true);
            }}
            disabled={
              !selectedContact ||
              isLoading ||
              showForm ||
              showUploader ||
              showProduceChoice ||
              showProductionList ||
              showSigningList ||
              isProducing
            }
            variant="outline"
            size="sm"
          >
            Order
          </Button>
          <SelectButton
            label="Quality"
            disabled={
              !selectedContact ||
              isLoading ||
              showForm ||
              showUploader ||
              showProduceChoice ||
              showProductionList ||
              showSigningList ||
              isProducing
            }
            items={[
              {
                label: "Meeting Minutes",
                description: "Logg av møte med kunde",
                icon: <ClipboardList size={14} />,
                onSelect: () => {
                  setActiveQualityForm("meetingMinutes");
                  setShowForm(true);
                },
              },
              {
                label: "Suitability (NEW)",
                description: hasSuitabilityDocument
                  ? "Slett eksisterende egnethetsattest før du oppretter en ny"
                  : "Vurder egnethet med rådgivers kommentar",
                icon: <Scale size={14} />,
                disabled: hasSuitabilityDocument,
                onSelect: () => {
                  if (hasSuitabilityDocument) return;
                  setActiveQualityForm("suitabilityNew");
                  setShowForm(true);
                },
              },
              {
                label: "Open customer instruction",
                description: "Åpen kundeordreinstruks",
                icon: <FileCheck2 size={14} />,
                onSelect: () => {
                  setActiveQualityForm("openCustomerInstruction");
                  setShowForm(true);
                },
              },
              {
                label: "Advisory against",
                description: "Dokumenter råd gitt mot kundens ønske",
                icon: <Ban size={14} />,
                onSelect: () => {
                  setActiveQualityForm("advisoryAgainst");
                  setShowForm(true);
                },
              },
            ]}
          />
          <SelectButton
            label="Kundeprofil"
            disabled={
              !selectedContact ||
              isLoading ||
              showForm ||
              showUploader ||
              showProduceChoice ||
              showProductionList ||
              showSigningList ||
              isProducing
            }
            items={["1", "2", "3"].map((updateType) => {
              const selContactName = data?.contact?.name ?? "";
              const textMap: Record<string, string> = {
                "1": `Du er invitert hit for å fylle ut en kundeavtale for ${selContactName}. Prosesen vil omfatte både en formell kundeavtale med lovpålagte AML-spørsmål, i tillegg til en MIFID2-profil.`,
                "2": `Du er invitert hit for å oppdatere MIFID2-profilen til ${selContactName}.`,
                "3": `Du er invitert hit for å oppdatere kundeavtale med lovpålagte AML-spørsmål til ${selContactName}.`,
              };
              const labelMap: Record<string, string> = {
                "1": "Kundeprofil",
                "2": "MIFID2-profil",
                "3": "AML-spørsmål",
              };
              const descMap: Record<string, string> = {
                "1": "Begge – AML-spørsmål og MIFID2-profil",
                "2": "Oppdater investorprofil og risikovurdering",
                "3": "Oppdater kundeavtale med lovpålagte AML-spørsmål",
              };
              const iconMap: Record<string, React.ReactNode> = {
                "1": <Users size={14} />,
                "2": <TrendingUp size={14} />,
                "3": <ShieldCheck size={14} />,
              };
              const itemDisabled =
                updateType === "1"
                  ? hasKundeprofilDocument || hasMifid || hasKyc
                  : updateType === "2"
                  ? hasKundeprofilDocument || hasMifid
                  : hasKundeprofilDocument || hasKyc;
              const disabledDescription = hasKundeprofilDocument
                ? "Slett eksisterende Kundeprofil før du oppretter en ny"
                : updateType === "1" && (hasMifid || hasKyc)
                ? "Kan bare velge MIFID eller AML når ett av dem allerede finnes"
                : updateType === "2" && hasMifid
                ? "Slett eksisterende MIFID2-profil før du oppretter en ny"
                : updateType === "3" && hasKyc
                ? "Slett eksisterende AML-dokument før du oppretter en ny"
                : descMap[updateType];
              return {
                label: labelMap[updateType],
                description: disabledDescription,
                icon: iconMap[updateType],
                disabled: itemDisabled,
                onSelect: () => {
                  if (itemDisabled) return;
                  setShowOrderForm(false);
                  setActiveQualityForm(null);
                  if (contactId) {
                    const documentId = `kyc_${updateType}_${new Date().toISOString()}`;
                    setPendingKundeprofilDocumentId(documentId);
                    dispatch({
                      type: "ADD_DOCUMENT",
                      payload: {
                        contactId: String(contactId),
                        document: {
                          id: documentId,
                          docType: "kundeprofil",
                          processName: labelMap[updateType],
                          desc: descMap[updateType],
                          data: {
                            updateType,
                            selContactId: contactId,
                            selContactName,
                          },
                        },
                      },
                    });
                  }
                  setDocData({
                    formKey: "capguard-kycVer2",
                    title: labelMap[updateType],
                    toProduction: false,
                    extraValues: {
                      selContactId: contactId,
                      selContactName,
                      updateType,
                    },
                    inpData: {
                      userProfile,
                      type: "newCustomer",
                      loadType: "docProd",
                      selContactId: contactId,
                      selContactName,
                      updateType,
                      text: textMap[updateType] ?? "",
                    },
                  });
                  setShowForm(true);
                },
              };
            })}
          />
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
          <Button
            onClick={handleUploadClick}
            disabled={
              !selectedContact ||
              isLoading ||
              showForm ||
              showUploader ||
              showProduceChoice ||
              showProductionList
            }
            variant="outline"
            size="sm"
            icon={<Upload size={16} />}
            iconPosition="start"
          >
            Upload
          </Button>
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />
          <Button
            disabled={
              !selectedContact ||
              isLoading ||
              showForm ||
              showUploader ||
              showProduceChoice ||
              showProductionList ||
              showSigningList ||
              documents.length === 0
            }
            onClick={() => setShowProduceChoice(true)}
            variant="default"
            size="sm"
            icon={<File size={16} />}
          >
            Produce documents
          </Button>
        </div>
        {(showForm ||
          showUploader ||
          showProduceChoice ||
          showProductionList ||
          showSigningList ||
          isProducing) && (
          <Button
            icon={<X className="size-3" />}
            onClick={handleCancelForm}
            variant="outline"
            size="sm"
          >
            Cancel
          </Button>
        )}
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-hidden">
        {!selectedContact ? (
          <div className="h-full flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <Info
                size={48}
                className="mx-auto mb-4 text-gray-400 dark:text-gray-600"
                strokeWidth={1.5}
              />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                No Contact Selected
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please select a contact from the list to create and manage
                documents.
              </p>
            </div>
          </div>
        ) : showForm ? (
          <div className="h-full">{formView}</div>
        ) : showProduceChoice ? (
          <ProduceChoiceView
            contactValues={contactValues}
            documents={documents}
            advisorData={advisorData?.contact}
            modelsData={modelsData ?? []}
            securities={securities ?? []}
            profileData={profileData ?? []}
            onPreview={(list) => {
              setProductionDocList(list);
              setShowProduceChoice(false);
              setShowProductionList(true);
            }}
            onSign={(list) => {
              setProductionDocList(list);
              setShowProduceChoice(false);
              const contact = contactValues?.contact;
              const isCompanyContact = contact?.juridical?.code === "Company";
              const signees: Signee[] = isCompanyContact
                ? (contact?.representatives ?? [])
                    .filter((r) => r.contactId?.startsWith("F"))
                    .map((r) => ({
                      id: r.id,
                      contactId: r.contactId,
                      name: r.name,
                    }))
                : contact &&
                  String(contact.contactId ?? contact.id).startsWith("F")
                ? [
                    {
                      id: Number(contact.id),
                      contactId: String(contact.contactId ?? contact.id),
                      name: contact.name,
                    },
                  ]
                : [];
              setSigningSignees(signees);
              setShowSigningList(true);
            }}
          />
        ) : isProducing ? (
          <div className="h-full flex flex-col items-center justify-center gap-8 px-8">
            {/* Animated icon */}
            <LogoAnimation size={56} />

            {/* Text */}
            <div className="text-center space-y-2">
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                Producing documents
              </h3>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                This usually takes a few seconds…
              </p>
            </div>

            {/* Step indicators */}
            <div className="flex flex-col gap-2.5 w-full max-w-xs">
              {[
                { label: "Starting production process", delay: "0ms" },
                { label: "Generating documents", delay: "400ms" },
                { label: "Fetching produced files", delay: "800ms" },
              ].map((step, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 animate-pulse"
                  style={{ animationDelay: step.delay }}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-400 dark:bg-blue-500 flex-shrink-0" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {step.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div className="w-full max-w-xs h-1 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-400 dark:bg-blue-500 rounded-full"
                style={{
                  width: "60%",
                  animation: "progress-indeterminate 2s ease-in-out infinite",
                }}
              />
            </div>

            <style>{`
              @keyframes progress-indeterminate {
                0%   { transform: translateX(-100%); width: 60%; }
                50%  { transform: translateX(80%);   width: 60%; }
                100% { transform: translateX(200%);  width: 60%; }
              }
            `}</style>
          </div>
        ) : showProductionList ? (
          <ProductionListView
            productionDocList={productionDocList}
            onProduce={(_signees, ordered) =>
              handleStartProcess(false, [], ordered)
            }
          />
        ) : showSigningList ? (
          <ProductionListView
            productionDocList={productionDocList}
            actionLabel="Send til signering"
            signees={signingSignees}
            onProduce={(selectedSignees, ordered) =>
              handleStartProcess(true, selectedSignees, ordered)
            }
          />
        ) : showUploader ? (
          <DocumentUploader onSubmit={handleSubmitUploadedDocuments} />
        ) : (
          <div className="flex h-full overflow-hidden">
            {/* Left: document list */}
            <div className="flex-1 p-5 overflow-y-auto min-w-0">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex size-9 flex-shrink-0 items-center justify-center rounded-xl bg-gray-900 text-white shadow-sm dark:bg-gray-100 dark:text-gray-900">
                    <FileText size={16} strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-base font-semibold leading-tight text-gray-900 dark:text-gray-100">
                      Documents
                    </h2>
                    <p className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400">
                      Dokumenter knyttet til kunden
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
                    {(
                      [
                        ["all", "Alle", documentCounts.all],
                        ["orders", "Ordrer", documentCounts.orders],
                        ["other", "Andre", documentCounts.other],
                      ] as const
                    ).map(([value, label, count]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setDocumentFilter(value)}
                        className={cn(
                          "rounded-md px-2.5 py-1 text-[10px] font-semibold transition-colors",
                          documentFilter === value
                            ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100"
                            : "text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                        )}
                      >
                        {label}{" "}
                        <span className="ml-0.5 tabular-nums opacity-60">
                          {count}
                        </span>
                      </button>
                    ))}
                  </div>
                  <Button
                    icon={<Copy className="size-3" />}
                    onClick={handleCopyFormDocumentsJson}
                    variant="outline"
                    size="sm"
                    disabled={documents.length === 0}
                  >
                    Kopier JSON
                  </Button>
                </div>
              </div>

              {documents.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/70 px-8 py-14 text-center dark:border-gray-700 dark:bg-gray-900/50">
                  <FileText
                    size={32}
                    className="mx-auto mb-3 text-gray-300 dark:text-gray-600"
                    strokeWidth={1.5}
                  />
                  <p className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    No documents yet
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Select a document type to get started
                  </p>
                </div>
              ) : visibleDocuments.length === 0 ? (
                <div className="flex min-h-40 items-center justify-center rounded-lg border border-dashed border-gray-300 text-center dark:border-gray-700">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Ingen dokumenter i denne visningen
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Velg Alle for å se hele dokumentlisten.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="hidden grid-cols-[minmax(0,1.6fr)_minmax(0,1.4fr)_96px] items-center gap-4 border-b bg-gray-50 py-2 pl-5 pr-4 text-[9px] font-bold uppercase tracking-widest text-gray-400 md:grid dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-500">
                    <span>Dokument</span>
                    <span>Detaljer</span>
                    <span className="text-right">Handlinger</span>
                  </div>
                  <div>
                    {visibleDocuments.map((doc) => (
                      <DocumentItem
                        key={doc.id}
                        doc={doc}
                        onDelete={handleDeleteDocument}
                        onEdit={handleEditOrderDoc}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right: SuitStatement parameter inspector */}
            {selectedContact && contactValues?.contact && (
              <>
                {/* Test-parametre panel — hidden, restore by uncommenting */}
                {isAdmin && (
                  <SuitParamEditorPanel
                    profileMap={baseProfileMap}
                    overrides={paramOverrides}
                    onChange={handleParamOverrideChange}
                    isOrg={
                      contactValues.contact.juridical?.juridicalType !==
                      "PRIVATE_PERSON"
                    }
                  />
                )}
                <SuitStatementPreviewPanel
                  contact={contactValues.contact}
                  baseProfileMap={baseProfileMap}
                  paramOverrides={effectiveParamOverrides}
                  activePorts={(contactValues.contact.portfolios ?? []).filter(
                    (p) => p.status === "A"
                  )}
                  pendingOrders={pendingOrders}
                  modelsData={modelsData ?? []}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

DocProducerContent.displayName = "DocProducerContent";

// ── SuitStatementPreviewPanel ────────────────────────────────────────────────

interface SuitStatementPreviewPanelProps {
  contact: Contact;
  baseProfileMap: ProfileMap;
  paramOverrides: Record<string, string>;
  activePorts: Portfolio[];
  pendingOrders: PendingOrder[];
  modelsData: QueryModelPosition[];
}

const suitStatementInstance = new SuitStatement();

// Module-level components — must NOT be defined inside render functions
// because React uses reference equality for component types; re-defining them
// on every render causes unmount→remount of their children (closing dropdowns).
const SPRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-[9px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide leading-none">
      {label}
    </span>
    {children}
  </div>
);

const SPSection = ({ title }: { title: string }) => (
  <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 pb-0.5 mb-1 mt-2 first:mt-0">
    {title}
  </p>
);

// ── SuitParamEditorPanel ──────────────────────────────────────────────────────

interface SuitParamEditorPanelProps {
  profileMap: ProfileMap;
  overrides: Record<string, string>;
  onChange: (key: string, value: string) => void;
  isOrg: boolean;
}

const SuitParamEditorPanel = memo(
  ({ profileMap, overrides, onChange, isOrg }: SuitParamEditorPanelProps) => {
    /** Effective value: typed-prefix key has priority over legacy untyped key. */
    const val = (key: string): string => {
      if (key in overrides) return overrides[key];
      const typedOverride = Object.keys(overrides).find(
        (k) => k.endsWith(`_${key}`) && overrides[k] !== ""
      );
      if (typedOverride) return overrides[typedOverride];
      // typed-prefix variant takes priority (new data beats stale legacy data)
      const suffix = `_${key}`;
      for (const k of Object.keys(profileMap)) {
        if (k.startsWith("contact.cgProfiling.") && k.endsWith(suffix)) {
          const v = profileMap[k];
          if (v !== null && v !== undefined && v !== "") return String(v);
        }
      }
      // legacy fallback: short key or full cgProfiling key
      const direct =
        profileMap[key] ?? profileMap[`contact.cgProfiling.${key}`] ?? null;
      if (direct !== null && direct !== undefined && direct !== "")
        return String(direct);
      return "";
    };

    // Like val(), but used when the override key is synthetic (not a real profile attribute).
    const valOr = (overrideKey: string, profileKey: string): string => {
      if (overrideKey in overrides) return overrides[overrideKey];
      // val() already handles typed-prefix scan + legacy fallback for both short and full keys
      return val(overrideKey) || val(profileKey);
    };

    const overrideCount = Object.keys(overrides).length;

    const normalizeText = (s: string): string =>
      s
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[–—-]/g, "-")
        .replace(/[\/]/g, " ")
        .replace(/[()/%]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();

    const FIELD_VALUE_ALIASES: Record<string, Record<string, string>> = {
      // Legacy org values from cgProfiling -> current option values.
      mifidIncomeStability: {
        stabil: "1",
        "helt stabil": "1",
        "noe variabel": "2",
        "noe variabel pluss minus 20": "2",
        "ganske variabel pluss minus 50": "3",
        "variabel uforutsigbar": "3",
        "variabel/uforutsigbar": "3",
        "helt variabel": "5",
      },
      mifidValueDeclineImpact: {
        lav: "1",
        moderat: "2",
        hoy: "3",
        stor: "3",
        "svaert hoy": "4",
        "svart hoy": "4",
        "svaert stor": "4",
      },
      mifidRiskToleranceVolatility: {
        lav: "1",
        moderat: "2",
        hoy: "3",
        offensiv: "4",
      },
      mifidMarketDropResponse: {
        "selge for a redusere tap": "Selge",
        selge: "Selge",
        "gjore ingenting og vente": "Vente",
        vente: "Vente",
        "vurdere a kjope mer": "Kjøpe mer / rebalansere",
        "rebalansere oke eksponering": "Kjøpe mer / rebalansere",
        "kjope mer rebalansere": "Kjøpe mer / rebalansere",
      },
      // HTML profile stores label text for these fields
      mifidEarliestWithdrawal: {
        "under ett ar": "1",
        "fra 1 5 ar": "2",
        "fra 1-5 ar": "2",
        "mer enn 5 ar": "3",
        "5 10 ar": "3",
        "mer enn 10 ar": "3",
        "mer enn 15 ar generasjonsforvaltning": "5",
      },
      mifidMainInvestmentPurpose: {
        kapitalbevaring: "1",
        "stabil avkastning": "2",
        "moderat vekst": "3",
        "langsiktig vekst": "4",
      },
      mifidLiquidityBuffer: {
        "under 250k": "<250K",
        "250k 1 mnok": "250K-1 MNOK",
        "250k-1 mnok": "250K-1 MNOK",
        "1 3 mnok": "1-3 MNOK",
        "1-3 mnok": "1-3 MNOK",
        "mer enn 3 mnok": ">3 MNOK",
      },
    };

    const toOptionValue = (
      fieldKey: string,
      raw: string,
      opts: readonly { label: string; value: string }[]
    ): string => {
      if (!raw) return "";
      if (opts.some((o) => o.value === raw)) return raw;

      const alias = FIELD_VALUE_ALIASES[fieldKey]?.[normalizeText(raw)];
      if (alias && opts.some((o) => o.value === alias)) return alias;

      const byLabel = opts.find((o) => o.label === raw);
      if (byLabel) return byLabel.value;

      const normRaw = normalizeText(raw);
      const fuzzy = opts.find(
        (o) =>
          normalizeText(o.value) === normRaw ||
          normalizeText(o.label) === normRaw
      );
      return fuzzy ? fuzzy.value : raw;
    };

    const selectCls =
      "w-full text-[10px] rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500";

    // Use a plain function (not a component) so React doesn't unmount/remount
    // the <select> on every re-render — that would close the dropdown.
    const mkSel = (
      k: string,
      opts: readonly { label: string; value: string }[]
    ) => (
      <select
        key={k}
        value={toOptionValue(k, val(k), opts)}
        onChange={(e) => onChange(k, e.target.value)}
        className={cn(
          selectCls,
          k in overrides ? "ring-1 ring-amber-400 border-amber-400" : ""
        )}
      >
        <option value="">—</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );

    // Select with fallback profile key when override key is synthetic.
    const mkSelOr = (
      overrideKey: string,
      profileKey: string,
      opts: readonly { label: string; value: string }[]
    ) => (
      <select
        key={overrideKey}
        value={toOptionValue(overrideKey, valOr(overrideKey, profileKey), opts)}
        onChange={(e) => onChange(overrideKey, e.target.value)}
        className={cn(
          selectCls,
          overrideKey in overrides
            ? "ring-1 ring-amber-400 border-amber-400"
            : ""
        )}
      >
        <option value="">—</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );

    // Row and Section are defined at module level (SPRow / SPSection) to keep
    // stable component identity across renders.

    /** Plain-function text input (same anti-pattern rule as mkSel). */
    const mkText = (k: string, placeholder?: string) => (
      <input
        key={k}
        type="text"
        value={val(k)}
        onChange={(e) => onChange(k, e.target.value)}
        placeholder={placeholder}
        className={cn(
          selectCls,
          k in overrides ? "ring-1 ring-amber-400 border-amber-400" : ""
        )}
      />
    );

    /** Plain-function number input. */
    const mkNum = (k: string, placeholder = "0") => (
      <input
        key={k}
        type="number"
        step="any"
        value={val(k)}
        onChange={(e) => {
          const raw = e.target.value;
          // Strip leading zeros (e.g. "0100" → "100"), but keep "0" and decimals intact
          const cleaned = raw.replace(/^0+(?=\d)/, "");
          onChange(k, cleaned);
        }}
        placeholder={placeholder}
        className={cn(
          selectCls,
          k in overrides ? "ring-1 ring-amber-400 border-amber-400" : ""
        )}
      />
    );

    return (
      <div className="w-72 xl:w-80 flex-shrink-0 border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 overflow-y-auto">
        <div className="p-3 space-y-1.5">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
              <Pencil size={12} className="text-blue-500" />
              Test-parametre
            </h3>
            {overrideCount > 0 && (
              <button
                onClick={() =>
                  Object.keys(overrides).forEach((k) => onChange(k, ""))
                }
                className="text-[9px] text-amber-600 dark:text-amber-400 hover:underline"
                title="Nullstill alle overstyringer"
              >
                Nullstill ({overrideCount})
              </button>
            )}
          </div>

          {/* ── Risiko ── */}
          <SPSection title="Risiko" />
          <SPRow label="Komfort med svingninger">
            {mkSel(
              "mifidRiskToleranceVolatility",
              isOrg ? ORG_RISK_COMFORT_OPTIONS : RISK_COMFORT_OPTIONS
            )}
          </SPRow>
          <SPRow label="Reaksjon ved 30 % markedsfall">
            {mkSel("mifidMarketDropResponse", MARKET_DROP_RESPONSE_OPTIONS)}
          </SPRow>

          {/* ── Investorprofil ── */}
          <SPSection title="Investorprofil" />
          <SPRow
            label={isOrg ? "Investeringshorisont" : "Tidligst ta ut penger"}
          >
            {mkSelOr(
              "mifidInvestmentHorizon",
              "mifidEarliestWithdrawal",
              ORG_INVESTMENT_HORIZON_OPTIONS
            )}
          </SPRow>
          <SPRow label="Investeringsmål">
            {isOrg
              ? mkSelOr(
                  "mifidInvestmentGoal",
                  "mifidMainInvestmentPurpose",
                  ORG_INVESTMENT_GOAL_OPTIONS
                )
              : mkSel("mifidMainInvestmentPurpose", INVESTMENT_GOAL_OPTIONS)}
          </SPRow>
          <SPRow label="Likviditetsbehov">
            {mkSel(
              "mifidLiquidityNeed",
              isOrg ? ORG_LIQUIDITY_OPTIONS : LIQUIDITY_OPTIONS
            )}
          </SPRow>
          <SPRow label="Evne til å håndtere verdifall 20-30%">
            {mkSel("mifidValueDeclineImpact", VALUE_DECLINE_IMPACT_OPTIONS)}
          </SPRow>
          <SPRow label="Inntektsstabilitet">
            {mkSel("mifidIncomeStability", INCOME_STABILITY_OPTIONS)}
          </SPRow>

          {/* ── Instrumenter ── */}
          <SPSection title="Instrumenter  (K = kunnskap · E = erfaring)" />
          {MIFID_INSTRUMENT_ROWS.map((r) => (
            <div
              key={r.label}
              className="rounded-lg border border-gray-200 dark:border-gray-700 p-1.5 space-y-1"
            >
              <p className="text-[9px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                {r.label}
              </p>
              <div className="grid grid-cols-2 gap-1">
                <SPRow label="Kunnskap">
                  {mkSel(r.knowledgeField, KNOWLEDGE_COLUMNS)}
                </SPRow>
                <SPRow label="Erfaring">
                  {mkSel(r.experienceField, EXPERIENCE_COLUMNS)}
                </SPRow>
                <SPRow label="Frekvens">
                  {mkSel(r.tradingFrequencyField, TRADING_FREQUENCY_COLUMNS)}
                </SPRow>
                <SPRow label="Periode">
                  {mkSel(r.periodField, PERIOD_COLUMNS)}
                </SPRow>
              </div>
            </div>
          ))}

          {/* ── Bakgrunn ── */}
          <SPSection title="Bakgrunn og erfaring" />
          <SPRow label="Relevant erfaring / utdanning">
            {mkText("mifidRelevantExperienceEducation", "Fri tekst...")}
          </SPRow>

          {/* ── ESG ── */}
          <SPSection title="ESG / Bærekraft" />
          <SPRow label="ESG-preferanser utover grunnleggende">
            {mkSel("mifidEsgPreference", ESG_PREF_OPTIONS)}
          </SPRow>
          <SPRow label="ESG-preferansetype">
            {mkText("mifidEsgPreferenceType", "Beskriv preferansetype...")}
          </SPRow>

          {/* ── Finansiell situasjon ── */}
          <SPSection title="Finansiell situasjon" />
          {isOrg ? (
            <>
              <SPRow label="Total netto formue (NOK)">
                {mkNum("mifidTotalNetWealth")}
              </SPRow>
              <SPRow label="Investerbare likvide midler (NOK)">
                {mkNum("mifidLiquidInvestableWealth")}
              </SPRow>
              <SPRow label="Planlagt kapitalbehov neste 2 år (NOK)">
                {mkNum("mifidLiquidityBuffer")}
              </SPRow>
            </>
          ) : (
            <>
              <SPRow label="Total netto formue (NOK)">
                {mkNum("mifidTotalNetWealth")}
              </SPRow>
              <SPRow label="Investerbare likvide midler (NOK)">
                {mkNum("mifidLiquidInvestableWealth")}
              </SPRow>
              <SPRow label="Brutto årsinntekt (NOK)">
                {mkNum("mifidGrossAnnualIncome")}
              </SPRow>
              <SPRow label="Planlagt kapitalbehov neste 2 år (NOK)">
                {mkNum("mifidLiquidityBuffer")}
              </SPRow>
            </>
          )}
          <SPRow label="Gjeldsbelastning">
            {mkSel("mifidDebtBurden", ORG_DEBT_BURDEN_OPTIONS)}
          </SPRow>

          {/* ── Aksjeandeler ── */}
          <SPSection title="Aksjeandeler %" />
          <SPRow label="Nåværende aksjeandel">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={valOr(
                "overrideCurrentEquityPct",
                "contact.profiling.aumStocks"
              )}
              onChange={(e) =>
                onChange("overrideCurrentEquityPct", e.target.value)
              }
              placeholder="0–100"
              className={cn(
                selectCls,
                "overrideCurrentEquityPct" in overrides
                  ? "ring-1 ring-amber-400 border-amber-400"
                  : ""
              )}
            />
          </SPRow>
          <SPRow label="Systemanbefalt aksjeandel">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={valOr(
                "overrideCGSysStockPart",
                "contact.cgProfiling.CGMaxStockPart"
              )}
              onChange={(e) =>
                onChange("overrideCGSysStockPart", e.target.value)
              }
              placeholder="0–100"
              className={cn(
                selectCls,
                "overrideCGSysStockPart" in overrides
                  ? "ring-1 ring-amber-400 border-amber-400"
                  : ""
              )}
            />
          </SPRow>
          <SPRow label="Min anbefalt aksjeandel">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={valOr(
                "overrideCGMinStockPart",
                "contact.cgProfiling.CGMinStockPart"
              )}
              onChange={(e) =>
                onChange("overrideCGMinStockPart", e.target.value)
              }
              placeholder="0–100"
              className={cn(
                selectCls,
                "overrideCGMinStockPart" in overrides
                  ? "ring-1 ring-amber-400 border-amber-400"
                  : ""
              )}
            />
          </SPRow>
          <SPRow label="Maks anbefalt aksjeandel">
            <input
              type="number"
              min={0}
              max={100}
              step={1}
              value={valOr(
                "overrideCGMaxStockPart",
                "contact.cgProfiling.CGMaxStockPart"
              )}
              onChange={(e) =>
                onChange("overrideCGMaxStockPart", e.target.value)
              }
              placeholder="0–100"
              className={cn(
                selectCls,
                "overrideCGMaxStockPart" in overrides
                  ? "ring-1 ring-amber-400 border-amber-400"
                  : ""
              )}
            />
          </SPRow>
        </div>
      </div>
    );
  }
);

SuitParamEditorPanel.displayName = "SuitParamEditorPanel";

const SuitStatementPreviewPanel = memo(
  ({
    contact,
    baseProfileMap,
    paramOverrides,
    activePorts,
    pendingOrders,
    modelsData,
  }: SuitStatementPreviewPanelProps) => {
    const { downloadReport, downloading } = useDownloadReport(
      `Capguard_SUIT_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`,
      false
    );
    const d = useMemo(
      () => {
        const stockParts = calcStockPartsFromPortfolios(
          activePorts,
          pendingOrders,
          modelsData
        );
        const suit = new AutoSuit(
          baseProfileMap,
          {
            ...paramOverrides,
            _stockCurrent: String(stockParts.current),
            _stockFin: String(stockParts.fin),
            _investmentAmount: String(
              pendingOrders.reduce(
                (sum, o) =>
                  sum + (o.amountToInvest ?? 0) + (o.oneOffAmount ?? 0),
                0
              )
            ),
          },
          contact,
          [],
          pendingOrders,
          modelsData
        );

        return {
          ...suit.toData(),
        };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        contact,
        baseProfileMap,
        paramOverrides,
        activePorts,
        pendingOrders,
        modelsData,
      ]
    );

    const handleDownloadReport = async () => {
      await downloadReport("Capguard - Suitability Juridical", { data: d });
    };

    return (
      <div className="w-72 xl:w-80 flex-shrink-0 border-l border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 overflow-y-auto">
        <div className="p-3">
          <h3 className="text-xs font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1.5 mb-2">
            <FileCheck2 size={12} className="text-emerald-500" />
            MiFID Egnethetsattest
            <Button
              loading={downloading}
              onClick={handleDownloadReport}
              variant="icon"
              icon={<DownloadCloud className="size-3" />}
              size="xs"
            />
          </h3>
          <table className="w-full text-[10px] border-collapse">
            <tbody>
              {Object.entries(d).map(([key, value]) => {
                if (value === null || value === undefined || value === "")
                  return null;
                const display =
                  typeof value === "boolean"
                    ? value
                      ? "Ja"
                      : "Nei"
                    : String(value);
                return (
                  <tr
                    key={key}
                    className="border-b border-gray-100 dark:border-gray-800"
                  >
                    <td className="py-0.5 pr-2 text-gray-500 dark:text-gray-400 align-top whitespace-nowrap">
                      {key}
                    </td>
                    <td className="py-0.5 text-gray-800 dark:text-gray-200 font-medium text-right break-all">
                      {display}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);

SuitStatementPreviewPanel.displayName = "SuitStatementPreviewPanel";

// DocumentUploader component
interface DocumentUploaderProps {
  onSubmit: (files: UploadedDocument[]) => void;
}

const DocumentUploader = ({ onSubmit }: DocumentUploaderProps) => {
  const limitedFileTypes = ["pdf", "doc", "docx", "jpg", "jpeg", "png"];
  const [files, setFiles] = useState<UploadedDocument[] | null>(null);
  const [isOver, setIsOver] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const disableSubmit = isLoading || !files || files.length === 0;

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const filesArray = Array.from(event.dataTransfer.files);
      const invalidFiles = filesArray.filter(
        (file) =>
          !limitedFileTypes.includes(
            file.name.split(".").pop()?.toLowerCase() || ""
          )
      );
      if (invalidFiles.length > 0) {
        toast.error(
          `Invalid file type. Only the following file types are allowed: ${limitedFileTypes
            .join(", ")
            .toUpperCase()}`,
          {
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            position: "top-right",
          }
        );
        setIsOver(false);
        return;
      }
      const base64Files = await Promise.all(
        filesArray.map(async (file) => {
          const base64 = await convertToBase64(file);
          return { file, stream: base64 };
        })
      );
      setFiles((prev) => {
        if (prev) {
          return [...prev, ...base64Files];
        } else {
          return base64Files;
        }
      });
      setIsLoading(false);
      event.dataTransfer.clearData();
    }
    setIsOver(false);
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadstart = () => {
        setIsLoading(true);
      };

      reader.onload = () => {
        resolve(reader.result as string);
      };

      reader.onerror = (error) => {
        setIsLoading(false);
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const filesArray = Array.from(e.target.files || []);
    const invalidFiles = filesArray.filter(
      (file) =>
        !limitedFileTypes.includes(
          file.name.split(".").pop()?.toLowerCase() || ""
        )
    );
    if (invalidFiles.length > 0) {
      toast.error(
        `Invalid file type. Only the following file types are allowed: ${limitedFileTypes
          .join(", ")
          .toUpperCase()}`
      );
      return;
    }
    const base64Files = await Promise.all(
      filesArray.map(async (file) => {
        const base64 = await convertToBase64(file);
        return { file, stream: base64 };
      })
    );
    setFiles((prev) => {
      if (prev) {
        return [...prev, ...base64Files];
      } else {
        return base64Files;
      }
    });
  };

  return (
    <div className="p-5 h-full flex flex-col">
      <div className="flex flex-col gap-4 max-w-3xl mx-auto w-full">
        <div className="flex justify-between items-center">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Upload Documents
          </h2>
          <input
            ref={fileInputRef}
            type="file"
            accept={limitedFileTypes.map((t) => `.${t}`).join(",")}
            onChange={handleInputChange}
            className="hidden"
            multiple
          />
          <Button onClick={handleInputClick} size="sm" variant="outline">
            Select files
          </Button>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsOver(false);
          }}
          onClick={(e) => {
            handleInputClick();
            e.stopPropagation();
          }}
          onDrop={(e) => handleDrop(e)}
          className={cn(
            "p-8 border-2 border-dashed rounded-xl transition-all cursor-pointer",
            isOver
              ? "border-primary bg-primary/5 dark:bg-primary/10"
              : "border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 bg-gray-50/50 dark:bg-gray-900/50"
          )}
        >
          {!files || files.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 text-center">
              <Upload
                size={48}
                className="text-gray-400 dark:text-gray-500"
                strokeWidth={1.5}
              />
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Drop files here or click to select
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Supports: {limitedFileTypes.join(", ").toUpperCase()}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {files.map((x, i) => {
                return (
                  <div
                    key={i}
                    onClick={(e) => e.stopPropagation()}
                    className="flex gap-3 px-3 py-2 items-center justify-between border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                  >
                    <div className="flex-1 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex items-center justify-center flex-shrink-0">
                        <File
                          size={16}
                          className="text-gray-600 dark:text-gray-400"
                        />
                      </div>
                      <div className="flex-1 flex flex-col min-w-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {x.file.name}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {x.file.size > 1024 * 1024
                            ? `${(x.file.size / (1024 * 1024)).toFixed(2)} MB`
                            : `${(x.file.size / 1024).toFixed(2)} KB`}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFiles((prev) => {
                          if (prev) {
                            return prev.filter((_, index) => index !== i);
                          }
                          return null;
                        });
                      }}
                      className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-md transition-colors flex-shrink-0"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {files && files.length > 0 && (
          <div className="flex justify-end">
            <Button
              disabled={disableSubmit}
              loading={isLoading}
              loadingPosition="start"
              variant="default"
              size="sm"
              onClick={() => {
                if (files) {
                  onSubmit(files);
                  setFiles(null);
                }
              }}
            >
              {isLoading
                ? "Processing..."
                : `Upload ${files.length} ${
                    files.length === 1 ? "file" : "files"
                  }`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// PDFViewer component with zoom and navigation controls
interface PDFViewerProps {
  documentPreview: string;
}

const PDFViewer = ({ documentPreview }: PDFViewerProps) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageInput, setPageInput] = useState<string>("1");
  const [scale, setScale] = useState<number>(1.4);
  const [rotation, setRotation] = useState<number>(0);
  const [continuousView, setContinuousView] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageInput("1");
    setIsLoading(false);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error("Error loading PDF:", error);
    toast.error("Failed to load PDF document");
    setIsLoading(false);
  };

  const goToPrevPage = () => {
    const newPage = Math.max(pageNumber - 1, 1);
    setPageNumber(newPage);
    setPageInput(String(newPage));
  };

  const goToNextPage = () => {
    const newPage = Math.min(pageNumber + 1, numPages);
    setPageNumber(newPage);
    setPageInput(String(newPage));
  };

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInput(e.target.value);
  };

  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(pageInput, 10);
    if (!isNaN(page) && page >= 1 && page <= numPages) {
      setPageNumber(page);
    } else {
      setPageInput(String(pageNumber));
    }
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  const rotatePage = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const toggleContinuousView = () => {
    setContinuousView((prev) => !prev);
  };

  const handleDownload = () => {
    try {
      const base64Content = documentPreview.includes(",")
        ? documentPreview.split(",")[1]
        : documentPreview;

      const byteCharacters = atob(base64Content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `document-${Date.now()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Could not download the document. Please try again.");
    }
  };

  // Convert base64 to data URL if needed
  const pdfData = documentPreview.startsWith("data:")
    ? documentPreview
    : `data:application/pdf;base64,${documentPreview}`;

  return (
    <div className="h-full flex flex-col border rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-900">
      {/* PDF Controls */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-white dark:bg-gray-800 flex-shrink-0">
        <div className="flex items-center gap-2">
          <Button
            onClick={goToPrevPage}
            disabled={pageNumber <= 1 || continuousView}
            variant="outline"
            size="sm"
            icon={<ChevronLeft size={16} />}
            title="Previous page"
          />
          <form
            onSubmit={handlePageInputSubmit}
            className="flex items-center gap-1"
          >
            <input
              type="text"
              value={pageInput}
              onChange={handlePageInputChange}
              onBlur={handlePageInputSubmit}
              disabled={continuousView}
              className="w-12 h-8 px-2 text-sm text-center border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">/</span>
            <span className="text-sm text-gray-700 dark:text-gray-300 min-w-[30px]">
              {numPages || "..."}
            </span>
          </form>
          <Button
            onClick={goToNextPage}
            disabled={pageNumber >= numPages || continuousView}
            variant="outline"
            size="sm"
            icon={<ChevronRight size={16} />}
            title="Next page"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={toggleContinuousView}
            variant={continuousView ? "default" : "outline"}
            size="sm"
            icon={<Rows2 size={16} />}
            title={
              continuousView
                ? "Switch to single page"
                : "Switch to continuous scroll"
            }
          />
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
          <Button
            onClick={rotatePage}
            variant="outline"
            size="sm"
            icon={<RotateCw size={16} />}
            title="Rotate 90°"
          />
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
          <Button
            onClick={zoomOut}
            disabled={scale <= 0.5}
            variant="outline"
            size="sm"
            icon={<ZoomOut size={16} />}
            title="Zoom out"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300 min-w-[60px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            onClick={zoomIn}
            disabled={scale >= 3.0}
            variant="outline"
            size="sm"
            icon={<ZoomIn size={16} />}
            title="Zoom in"
          />
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />
          <Button
            onClick={handleDownload}
            variant="default"
            size="sm"
            icon={<Download size={16} />}
            title="Download PDF"
          />
        </div>
      </div>

      {/* PDF Display */}
      <div className="flex-1 overflow-auto p-4 flex justify-center">
        {isLoading && (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <LogoAnimation size={32} />
            <div className="text-gray-500 dark:text-gray-400">
              Loading PDF...
            </div>
          </div>
        )}
        <Document
          file={pdfData}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <LogoAnimation size={32} />
              <div className="text-gray-500 dark:text-gray-400">
                Loading PDF...
              </div>
            </div>
          }
          error={
            <div className="flex items-center justify-center h-full">
              <div className="text-red-500 dark:text-red-400">
                Failed to load PDF document
              </div>
            </div>
          }
        >
          {continuousView ? (
            <div className="flex flex-col gap-4">
              {Array.from(new Array(numPages), (el, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  scale={scale}
                  rotate={rotation}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  className="shadow-lg"
                />
              ))}
            </div>
          ) : (
            <Page
              pageNumber={pageNumber}
              scale={scale}
              rotate={rotation}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              className="shadow-lg"
            />
          )}
        </Document>
      </div>
    </div>
  );
};
